import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ComposedChart, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Bar, Tooltip, Legend, Line, Area, PieChart, Pie, Cell, BarChart } from 'recharts';
import { auth } from '../firebase';
import { getCloudFunctionUrl } from '../config';
import Spinner from './Spinner';
import './WallStreetChart.css';

/**
 * Recharts aplica estilos inline en el tooltip (p. ej. backgroundColor #fff en DefaultTooltipContent)
 * y el wrapper hereda reglas globales con rgba. Forzamos capas opacas vía props para que no ganen
 * fondos semitransparentes.
 */
const RECHARTS_TOOLTIP_WRAPPER_OPAQUE = {
  backgroundColor: 'var(--wsc-tooltip-bg)',
  border: '1px solid var(--wsc-tooltip-border)',
  borderRadius: 8,
  opacity: 1,
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.45)',
};

const RECHARTS_TOOLTIP_CONTENT_OPAQUE = {
  backgroundColor: 'var(--wsc-tooltip-bg)',
  border: '1px solid var(--wsc-tooltip-border)',
  borderRadius: 8,
  color: 'var(--wsc-tooltip-text)',
  padding: '10px 12px',
  opacity: 1,
  whiteSpace: 'normal',
};

const RECHARTS_TOOLTIP_LABEL_OPAQUE = {
  color: 'var(--wsc-tooltip-label)',
  margin: 0,
};

const RECHARTS_TOOLTIP_ITEM_OPAQUE = {
  color: 'var(--wsc-tooltip-item)',
};

const wallStreetTooltipProps = {
  wrapperStyle: RECHARTS_TOOLTIP_WRAPPER_OPAQUE,
  contentStyle: RECHARTS_TOOLTIP_CONTENT_OPAQUE,
  labelStyle: RECHARTS_TOOLTIP_LABEL_OPAQUE,
  itemStyle: RECHARTS_TOOLTIP_ITEM_OPAQUE,
};

/**
 * Genera un ID seguro y único para un color, usado como referencia del `<linearGradient>`.
 */
const toGradientId = (color, suffix = '') =>
  `wscGrad-${String(color).replace(/[^a-zA-Z0-9]/g, '')}${suffix ? `-${suffix}` : ''}`;

/**
 * Construye una URL fill reference para un color dado.
 */
const gradientFill = (color, suffix = '') => `url(#${toGradientId(color, suffix)})`;

/**
 * Devuelve la lista de colores únicos conservando el orden.
 */
const uniqueColors = (colors) => Array.from(new Set(colors.filter(Boolean)));

/**
 * Mapeo dataKey → color base de la serie. Necesario porque cuando los `Bar`/`Area`
 * se pintan con un gradiente (`fill="url(#...)"`), Recharts pasa esa URL como
 * `entry.color` en el payload del tooltip, perdiéndose el color "real" para
 * estilizar texto/etiqueta. Este mapa permite recuperarlo.
 */
const SERIES_COLOR_BY_KEY = {
  salesAmount: '#3b82f6',
  costPrice: '#ef4444',
  profit: '#10b981',
  invoicedAmount: '#8b5cf6',
  unpaidAmount: '#ef4444',
  candleHigh: '#8b5cf6',
  mercadoPago: '#00a8ff',
  pedidos: '#ff9f43',
  fisicas: '#10ac84',
  total: '#94a3b8',
};

/**
 * Devuelve un color CSS válido para el texto/swatch del tooltip a partir de la
 * entrada del payload de Recharts. Si Recharts entregó una URL de gradiente
 * (`url(#...)`) se cae al mapa por dataKey, y como último recurso al `fill`
 * del payload o a un color por defecto.
 */
const resolveSeriesColor = (entry, fallback) => {
  const raw = entry?.color;
  if (raw && typeof raw === 'string' && !raw.startsWith('url(')) {
    return raw;
  }
  const byKey = entry?.dataKey ? SERIES_COLOR_BY_KEY[entry.dataKey] : null;
  if (byKey) return byKey;
  const payloadFill = entry?.payload?.[`${entry?.dataKey}Color`] || entry?.payload?.color;
  if (payloadFill && typeof payloadFill === 'string' && !payloadFill.startsWith('url(')) {
    return payloadFill;
  }
  return fallback || 'var(--wsc-tooltip-item)';
};

/**
 * Umbrales de negocio para clasificar indicadores. Centralizados para que tocarlos en un
 * único lugar actualice todas las reglas de insights/recomendaciones.
 */
const INSIGHT_THRESHOLDS = {
  MARGIN_EXCELLENT: 25,
  MARGIN_GOOD: 15,
  DIGITAL_ADOPTION_HIGH: 50,
  DISCOUNT_VS_PROFIT_HEAVY: 0.5,
  DAYS_WITHOUT_SALES_ALERT: 0.3,
};

/**
 * `<strong>` con color acento dinámico, respetando el tema (la variable CSS se
 * interpreta por `.wsc-accent-strong` en el stylesheet).
 */
const AccentText = ({ color, children }) => (
  <strong className="wsc-accent-strong" style={{ '--wsc-series-color': color }}>
    {children}
  </strong>
);

/**
 * Tarjeta estándar para las secciones de insights. Acepta `status` opcional
 * (`good` | `warn` | `bad`) para aplicar un borde lateral de acento.
 */
const InsightCard = ({ icon, title, status, children }) => (
  <div className={`insight-card${status ? ` insight-card--${status}` : ''}`}>
    <h5>{icon ? `${icon} ` : ''}{title}</h5>
    {children}
  </div>
);

/**
 * Renderiza `<linearGradient>` verticales por color único. Debe envolverse con `<defs>`
 * inline dentro del gráfico Recharts (Recharts no procesa componentes wrapper custom,
 * por eso no se puede wrappear también el `<defs>`).
 */
const renderColorGradients = (colors, suffix = '', topOpacity = 1, bottomOpacity = 0.55) =>
  uniqueColors(colors).map((color) => (
    <linearGradient
      key={toGradientId(color, suffix)}
      id={toGradientId(color, suffix)}
      x1="0"
      y1="0"
      x2="0"
      y2="1"
    >
      <stop offset="0%" stopColor={color} stopOpacity={topOpacity} />
      <stop offset="100%" stopColor={color} stopOpacity={bottomOpacity} />
    </linearGradient>
  ));

/** Vela: mecha = total consignado; cuerpo rojo = monto no cobrado. */
const ConsignmentCandle = (props) => {
  const { x, y, width, height, payload } = props;
  const total = Number(payload?.salesAmount) || 0;
  const unpaid = Math.min(Number(payload?.unpaidAmount) || 0, total);
  if (!Number.isFinite(x) || !Number.isFinite(y) || total <= 0 || height <= 0) return null;
  const scale = height / total;
  const unpaidHeight = Math.max(unpaid * scale, unpaid > 0 ? 3 : 0);
  const wickX = x + width / 2;
  const bodyWidth = Math.max(Math.min(width * 0.55, 18), 6);
  const bodyX = x + (width - bodyWidth) / 2;
  const fullyPaid = unpaid <= 0;
  return (
    <g>
      <line
        x1={wickX}
        x2={wickX}
        y1={y}
        y2={y + height}
        stroke={fullyPaid ? '#10b981' : '#8b5cf6'}
        strokeWidth={2}
      />
      {fullyPaid ? (
        <rect x={bodyX} y={y + height - 6} width={bodyWidth} height={6} rx={1} fill="#10b981" />
      ) : (
        <rect x={bodyX} y={y} width={bodyWidth} height={unpaidHeight} rx={1} fill="#ef4444" />
      )}
    </g>
  );
};

const CHART_CHANNEL_TABS = [
  { label: 'Todas las Ventas', index: 0 },
  { label: 'Ventas Físicas', index: 1 },
  { label: 'Ventas MercadoPago', index: 2 },
  { label: 'Ventas Pedidos', index: 3 },
  { label: 'Consignación', index: 4 },
];
const CHART_CHANNEL_LAST = CHART_CHANNEL_TABS.length - 1;

const WallStreetChart = () => {
  const [ventasMercadoPago, setVentasMercadoPago] = useState([]);
  const [ventasPedidos, setVentasPedidos] = useState([]);
  const [ventasFisicas, setVentasFisicas] = useState([]);
  const [startDateTime, setStartDateTime] = useState(getDefaultStartDateTime());
  const [endDateTime, setEndDateTime] = useState(getDefaultEndDateTime());
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [chartCarouselIndex, setChartCarouselIndex] = useState(0);
  const [chartGranularity, setChartGranularity] = useState('day');
  const [chartType, setChartType] = useState('bar');
  const [paymentPieMetric, setPaymentPieMetric] = useState('amount');
  const [activeLines, setActiveLines] = useState({
    salesAmount: false,
    costPrice: false,
    profit: false,
    invoicedAmount: false,
  });
  // Series cuya visualización (barras/área) está oculta desde la leyenda
  const [hiddenSeries, setHiddenSeries] = useState({
    salesAmount: false,
    costPrice: false,
    profit: false,
    invoicedAmount: false,
    unpaidAmount: false,
  });

  const fetchSalesData = useCallback(async (overrideStart, overrideEnd) => {
    const start = overrideStart != null ? overrideStart : startDateTime;
    const end = overrideEnd != null ? overrideEnd : endDateTime;
    setLoading(true);
    setErrorMessage(null);
    try {
      const user = auth.currentUser;
      if (!user) {
        setErrorMessage('Iniciá sesión para cargar el balance.');
        return;
      }
      const token = await user.getIdToken();
      let url;
      try {
        url = getCloudFunctionUrl('getWallStreetChartAggregates');
      } catch (e) {
        setErrorMessage('Falta configurar la URL de getWallStreetChartAggregates (REACT_APP_CF_* o BASE_URL en .env).');
        return;
      }
      const startMs = new Date(start).getTime();
      const endMs = new Date(end).getTime();
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ startMs, endMs }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json.message || json.error || `Error HTTP ${response.status}`);
      }
      if (!json.success) {
        throw new Error(json.message || 'No se pudieron cargar los agregados.');
      }
      const reviveDayRows = (arr) =>
        (Array.isArray(arr) ? arr : []).map((d) => ({
          ...d,
          dateObj: d.dateObj ? new Date(d.dateObj) : null,
        }));
      setVentasMercadoPago(reviveDayRows(json.mercadoPago));
      setVentasPedidos(reviveDayRows(json.pedidos));
      setVentasFisicas(reviveDayRows(json.fisicas));
      setErrorMessage(null);
    } catch (error) {
      console.error('Error fetching sales data:', error);
      setErrorMessage(error?.message || 'Error al cargar los datos de ventas. Verificá la conexión e intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [startDateTime, endDateTime]);

  const formatDate = (date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateRangeDisplay = (dateTimeStr) => {
    if (!dateTimeStr) return '—';
    const datePart = String(dateTimeStr).split('T')[0];
    const [y, m, d] = datePart.split('-');
    return d && m && y ? `${d}/${m}/${y}` : datePart;
  };

  const parseDateKey = (dateStr) => {
    if (!dateStr) return null;
    const parts = String(dateStr).split('/');
    if (parts.length !== 3) return null;
    const [d, m, y] = parts.map(Number);
    const date = new Date(y, m - 1, d);
    return isNaN(date.getTime()) ? null : date;
  };

  const getPeriodInfo = (dateObj, granularity) => {
    if (!dateObj) return null;
    const d = new Date(dateObj);
    if (isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = d.getMonth();
    const day = d.getDate();
    switch (granularity) {
      case 'year':
        return { key: String(y), label: String(y), sortDate: new Date(y, 0, 1) };
      case 'month': {
        const monthKey = `${String(m + 1).padStart(2, '0')}/${y}`;
        const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        return { key: monthKey, label: `${monthNames[m]} ${y}`, sortDate: new Date(y, m, 1) };
      }
      case 'week': {
        const tmp = new Date(Date.UTC(y, m, day));
        const dayNum = tmp.getUTCDay() || 7;
        tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
        const weekNo = Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
        const weekYear = tmp.getUTCFullYear();
        const weekStart = new Date(y, m, day - ((new Date(y, m, day).getDay() || 7) - 1));
        return {
          key: `S${String(weekNo).padStart(2, '0')}/${weekYear}`,
          label: `Sem ${weekNo} · ${weekYear}`,
          sortDate: weekStart,
        };
      }
      case 'day':
      default: {
        const key = `${String(day).padStart(2, '0')}/${String(m + 1).padStart(2, '0')}/${y}`;
        return { key, label: key, sortDate: new Date(y, m, day) };
      }
    }
  };

  const aggregateByGranularity = useCallback((data, granularity) => {
    if (!Array.isArray(data) || data.length === 0) return data || [];
    if (granularity === 'day') return data;
    const map = new Map();
    data.forEach((item) => {
      const dateObj = item.dateObj instanceof Date ? item.dateObj : parseDateKey(item.date);
      const info = getPeriodInfo(dateObj, granularity);
      if (!info) return;
      if (!map.has(info.key)) {
        map.set(info.key, {
          date: info.label,
          dateObj: info.sortDate,
          salesAmount: 0,
          costPrice: 0,
          productCost: 0,
          profit: 0,
          subtotal: 0,
          discountAmount: 0,
          shippingCost: 0,
          discountPercentage: 0,
          salesCount: 0,
          productsCount: 0,
          invoicedAmount: 0,
          invoicedCount: 0,
          unpaidAmount: 0,
          unpaidCount: 0,
        });
      }
      const bucket = map.get(info.key);
      bucket.salesAmount += item.salesAmount || 0;
      bucket.costPrice += item.costPrice || 0;
      bucket.productCost += item.productCost || 0;
      bucket.profit += item.profit || 0;
      bucket.subtotal += item.subtotal || 0;
      bucket.discountAmount += item.discountAmount || 0;
      bucket.shippingCost += item.shippingCost || 0;
      bucket.discountPercentage = Math.max(bucket.discountPercentage, item.discountPercentage || 0);
      bucket.salesCount += item.salesCount || 0;
      bucket.productsCount += item.productsCount || 0;
      bucket.invoicedAmount += item.invoicedAmount || 0;
      bucket.invoicedCount += item.invoicedCount || 0;
      bucket.unpaidAmount += item.unpaidAmount || 0;
      bucket.unpaidCount += item.unpaidCount || 0;
    });
    return Array.from(map.values()).sort((a, b) => {
      const at = a.dateObj instanceof Date ? a.dateObj.getTime() : 0;
      const bt = b.dateObj instanceof Date ? b.dateObj.getTime() : 0;
      return at - bt;
    });
  }, []);

  const GRANULARITY_OPTIONS = useMemo(() => ([
    { value: 'day', label: 'Día' },
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mes' },
    { value: 'year', label: 'Año' },
  ]), []);

  const CHART_TYPE_OPTIONS = useMemo(() => ([
    { value: 'bar', label: 'Barras' },
    { value: 'area', label: 'Área' },
  ]), []);

  const actualDateRange = useMemo(() => {
    const allDateStr = [
      ...ventasMercadoPago.map(v => v.date),
      ...ventasPedidos.map(v => v.date),
      ...ventasFisicas.map(v => v.date)
    ];
    const dates = allDateStr.map(parseDateKey).filter(Boolean);
    if (dates.length === 0) return null;
    const min = new Date(Math.min(...dates.map(d => d.getTime())));
    const max = new Date(Math.max(...dates.map(d => d.getTime())));
    return { min, max };
  }, [ventasMercadoPago, ventasPedidos, ventasFisicas]);

  const formatNumber = (price) => {
    return price
      .toFixed(2)
      .replace('.', ',')
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  // Función para analizar métodos de pago solo de ventas físicas
  const analyzePaymentMethods = useCallback(() => {
    // Solo analizar ventas físicas que tienen métodos de pago
    const physicalSalesWithPayments = ventasFisicas.filter(day => day.paymentMethods);
    
    const paymentAnalysis = {
      efectivo: { totalAmount: 0, totalCount: 0, totalProducts: 0 },
      transferencia: { totalAmount: 0, totalCount: 0, totalProducts: 0 },
      debito: { totalAmount: 0, totalCount: 0, totalProducts: 0 },
      credito: { totalAmount: 0, totalCount: 0, totalProducts: 0 },
      consignacion: { totalAmount: 0, totalCount: 0, totalProducts: 0 }
    };

    physicalSalesWithPayments.forEach(day => {
      Object.keys(paymentAnalysis).forEach(method => {
        if (day.paymentMethods[method]) {
          paymentAnalysis[method].totalAmount += day.paymentMethods[method].amount || 0;
          paymentAnalysis[method].totalCount += day.paymentMethods[method].count || 0;
          paymentAnalysis[method].totalProducts += day.paymentMethods[method].products || 0;
        }
      });
    });

    return paymentAnalysis;
  }, [ventasFisicas]);

  // Nueva función para agregar datos por mes incluyendo métodos de pago solo de ventas físicas
  const aggregateDataByMonth = useCallback((mercadoPagoData, pedidosData, fisicasData) => {
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const monthlyData = {};

    const processData = (data, channel) => {
      data.forEach(item => {
        if (item.dateObj) {
          const month = item.dateObj.getMonth();
          const year = item.dateObj.getFullYear();
          const monthKey = `${monthNames[month]} ${year}`;
          
          if (!monthlyData[monthKey]) {
            monthlyData[monthKey] = {
              month: monthKey,
              monthIndex: month,
              year: year,
              mercadoPago: 0,
              pedidos: 0,
              fisicas: 0,
              totalSales: 0,
              totalCost: 0,
              totalProfit: 0,
              totalDiscounts: 0,
              transactionCount: 0,
              productsCount: 0,
              invoicedAmount: 0,
              invoicedCount: 0,
              paymentMethods: {
                efectivo: 0,
                transferencia: 0,
                debito: 0,
                credito: 0,
                consignacion: 0
              }
            };
          }
          
          monthlyData[monthKey][channel] += item.salesAmount || 0;
          monthlyData[monthKey].totalSales += item.salesAmount || 0;
          monthlyData[monthKey].totalCost += item.costPrice || 0;
          monthlyData[monthKey].totalProfit += item.profit || 0;
          monthlyData[monthKey].totalDiscounts += item.discountAmount || 0;
          monthlyData[monthKey].transactionCount += 1;
          monthlyData[monthKey].productsCount += item.productsCount || 0;

          // Facturación: todos los canales (MercadoPago, Pedidos, Físicas)
          monthlyData[monthKey].invoicedAmount += item.invoicedAmount || 0;
          monthlyData[monthKey].invoicedCount += item.invoicedCount || 0;
          // Solo métodos de pago para ventas físicas
          if (channel === 'fisicas' && item.paymentMethods) {
            Object.keys(item.paymentMethods).forEach(method => {
              if (item.paymentMethods[method] && monthlyData[monthKey].paymentMethods[method] !== undefined) {
                monthlyData[monthKey].paymentMethods[method] += item.paymentMethods[method].amount || 0;
              }
            });
          }
        }
      });
    };

    processData(mercadoPagoData, 'mercadoPago');
    processData(pedidosData, 'pedidos');
    processData(fisicasData, 'fisicas');

    return Object.values(monthlyData).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.monthIndex - b.monthIndex;
    });
  }, []);

  const monthlyData = useMemo(() => 
    aggregateDataByMonth(ventasMercadoPago, ventasPedidos, ventasFisicas),
    [ventasMercadoPago, ventasPedidos, ventasFisicas, aggregateDataByMonth]
  );

  const paymentMethodsAnalysis = useMemo(() => analyzePaymentMethods(), [analyzePaymentMethods]);

  const tooltipPeriodLabel = useMemo(() => ({
    day: 'Fecha',
    week: 'Semana',
    month: 'Mes',
    year: 'Año',
  }[chartGranularity] || 'Período'), [chartGranularity]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const seenKeys = new Set();
      const dedupedPayload = payload.filter((entry) => {
        if (!entry || entry.dataKey == null) return true;
        if (seenKeys.has(entry.dataKey)) return false;
        seenKeys.add(entry.dataKey);
        return true;
      });
      return (
        <div className="wsc-custom-tooltip">
          <p className="label">{`${tooltipPeriodLabel}: ${label}`}</p>
          <p className="wsc-tooltip-row wsc-tooltip-row--muted">
            Operaciones: <strong>{data.salesCount ?? 0}</strong>
          </p>
          <p className="wsc-tooltip-row wsc-tooltip-row--muted">
            Productos vendidos: <strong>{data.productsCount ?? 0}</strong>
          </p>
          {dedupedPayload.map((entry, index) => (
            <React.Fragment key={`item-${index}`}>
              <p
                className={`wsc-tooltip-series${activeLines[entry.dataKey] ? ' wsc-tooltip-series--active' : ''}`}
                style={{ '--wsc-series-color': resolveSeriesColor(entry) }}
              >
                <span className="wsc-tooltip-swatch" aria-hidden="true" />
                <span className="wsc-tooltip-series-label">
                  {`${entry.name}: $ ${formatNumber(entry.value)}`}
                </span>
              </p>
              {entry.dataKey === 'costPrice' && data.shippingCost > 0 && (
                <>
                  <p className="wsc-tooltip-row wsc-tooltip-row--soft wsc-tooltip-row--indent">
                    ↳ Costo productos: ${formatNumber(data.productCost ?? 0)}
                  </p>
                  <p className="wsc-tooltip-row wsc-tooltip-row--soft wsc-tooltip-row--indent">
                    ↳ Costo envíos: ${formatNumber(data.shippingCost)}
                  </p>
                </>
              )}
            </React.Fragment>
          ))}
          {data.unpaidAmount > 0 && (
            <p className="wsc-tooltip-row wsc-tooltip-row--discount">
              No cobrada: ${formatNumber(data.unpaidAmount)}
              {data.unpaidCount > 0 && ` (${data.unpaidCount} ${data.unpaidCount === 1 ? 'venta' : 'ventas'})`}
            </p>
          )}
          {data.discountAmount > 0 && (
            <p className="wsc-tooltip-row wsc-tooltip-row--discount">
              Descuentos: -${formatNumber(data.discountAmount)}
              {data.discountPercentage > 0 && ` (${data.discountPercentage.toFixed(1)}%)`}
            </p>
          )}
          {data.shippingCost > 0 && (
            <p className="wsc-tooltip-row wsc-tooltip-row--secondary-muted">
              Costo de envío: ${formatNumber(data.shippingCost)}
              <span className="wsc-tooltip-row-hint"> (cobrado al cliente y pagado al transporte)</span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const CustomCombinedTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const seenKeys = new Set();
      const dedupedPayload = payload.filter((entry) => {
        if (!entry || entry.dataKey == null) return true;
        if (seenKeys.has(entry.dataKey)) return false;
        seenKeys.add(entry.dataKey);
        return true;
      });
      return (
        <div className="wsc-custom-tooltip">
          <p className="label">{`Fecha: ${label}`}</p>
          {dedupedPayload.map((entry, index) => (
            <p
              key={`item-${index}`}
              className={`wsc-tooltip-series${activeLines[entry.dataKey] ? ' wsc-tooltip-series--active' : ''}`}
              style={{ '--wsc-series-color': resolveSeriesColor(entry) }}
            >
              <span className="wsc-tooltip-swatch" aria-hidden="true" />
              <span className="wsc-tooltip-series-label">
                {`${entry.name}: $ ${formatNumber(entry.value)}`}
              </span>
            </p>
          ))}
          {data.total != null && (
            <p className="wsc-tooltip-total-line">
              <span className="wsc-tooltip-swatch wsc-tooltip-swatch--total" aria-hidden="true" />
              Total día: $ {formatNumber(data.total)}
            </p>
          )}
          <p className="wsc-tooltip-row wsc-tooltip-row--muted">
            Operaciones: <strong>{data.operationsCount ?? 0}</strong>
          </p>
          <p className="wsc-tooltip-row wsc-tooltip-row--muted">
            Productos vendidos: <strong>{data.productsCount ?? 0}</strong>
          </p>
        </div>
      );
    }
    return null;
  };

  const handleLegendClick = (dataKey) => {
    setActiveLines((prevState) => ({
      ...prevState,
      [dataKey]: !prevState[dataKey],
    }));
  };

  // Oculta/muestra la visualización (barras o área) de una serie del gráfico
  const handleSeriesToggle = (dataKey) => {
    setHiddenSeries((prevState) => {
      const nextHidden = !prevState[dataKey];
      // Si se oculta la serie, también se apaga su línea de tendencia para evitar
      // que quede una serie "fantasma" sin su barra/área asociada.
      if (nextHidden) {
        setActiveLines((lines) => ({ ...lines, [dataKey]: false }));
      }
      return { ...prevState, [dataKey]: nextHidden };
    });
  };

  // Cargar datos solo al montar; al cambiar fechas no se recarga hasta pulsar "Actualizar datos"
  useEffect(() => {
    fetchSalesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTotalDaysInRange = useCallback(() => {
    if (!startDateTime || !endDateTime) return 0;

    const start = new Date(startDateTime);
    const end = new Date(endDateTime);

    if (isNaN(start) || isNaN(end) || end < start) return 0;

    // Normalizar a días completos e incluir ambos extremos
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    const diffMs = endDay - startDay;
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.floor(diffMs / msPerDay) + 1;
  }, [startDateTime, endDateTime]);

  const getTotalMonthsInRange = useCallback(() => {
    if (!startDateTime || !endDateTime) return 0;

    const start = new Date(startDateTime);
    const end = new Date(endDateTime);

    if (isNaN(start) || isNaN(end) || end < start) return 0;

    const startYear = start.getFullYear();
    const startMonth = start.getMonth();
    const endYear = end.getFullYear();
    const endMonth = end.getMonth();

    return (endYear - startYear) * 12 + (endMonth - startMonth) + 1;
  }, [startDateTime, endDateTime]);

  const calculateTotals = useCallback((data) => {
    return data.reduce((totals, item) => {
      totals.salesAmount += item.salesAmount || 0;
      totals.costPrice += item.costPrice || 0;
      totals.productCost += item.productCost || 0;
      totals.profit += item.profit || 0;
      totals.subtotal += item.subtotal || 0;
      totals.discountAmount += item.discountAmount || 0;
      totals.shippingCost += item.shippingCost || 0;
      totals.discountPercentage = Math.max(totals.discountPercentage || 0, item.discountPercentage || 0);
      totals.invoicedAmount += item.invoicedAmount || 0;
      totals.invoicedCount += item.invoicedCount || 0;
      totals.salesCount += item.salesCount || 0;
      totals.productsCount += item.productsCount || 0;
      totals.unpaidAmount += item.unpaidAmount || 0;
      totals.unpaidCount += item.unpaidCount || 0;
      return totals;
    }, {
      salesAmount: 0,
      costPrice: 0,
      productCost: 0,
      profit: 0,
      subtotal: 0,
      discountAmount: 0,
      shippingCost: 0,
      discountPercentage: 0,
      invoicedAmount: 0,
      invoicedCount: 0,
      salesCount: 0,
      productsCount: 0,
      unpaidAmount: 0,
      unpaidCount: 0
    });
  }, []);

  const calculateDailyAverage = useCallback((total) => {
    const numberOfDays = getTotalDaysInRange();
    return numberOfDays > 0 ? total / numberOfDays : 0;
  }, [getTotalDaysInRange]);

  const calculateMonthlyAverage = useCallback((total) => {
    const numberOfMonths = getTotalMonthsInRange();
    return numberOfMonths > 0 ? total / numberOfMonths : 0;
  }, [getTotalMonthsInRange]);

  const totalsMercadoPago = useMemo(() => calculateTotals(ventasMercadoPago), [ventasMercadoPago, calculateTotals]);
  const totalsPedidos = useMemo(() => calculateTotals(ventasPedidos), [ventasPedidos, calculateTotals]);
  const totalsFisicas = useMemo(() => calculateTotals(ventasFisicas), [ventasFisicas, calculateTotals]);

  // Serie diaria combinada de todos los canales (Físicas + MercadoPago + Pedidos),
  // fusionada por fecha para reutilizar renderChart / aggregateByGranularity.
  const ventasTodas = useMemo(() => {
    const allDates = new Set([
      ...ventasMercadoPago.map(v => v.date),
      ...ventasPedidos.map(v => v.date),
      ...ventasFisicas.map(v => v.date),
    ]);

    const accumulate = (acc, item) => {
      if (!item) return acc;
      acc.salesAmount += item.salesAmount || 0;
      acc.costPrice += item.costPrice || 0;
      acc.productCost += item.productCost || 0;
      acc.profit += item.profit || 0;
      acc.subtotal += item.subtotal || 0;
      acc.discountAmount += item.discountAmount || 0;
      acc.shippingCost += item.shippingCost || 0;
      acc.discountPercentage = Math.max(acc.discountPercentage, item.discountPercentage || 0);
      acc.salesCount += item.salesCount || 0;
      acc.productsCount += item.productsCount || 0;
      acc.invoicedAmount += item.invoicedAmount || 0;
      acc.invoicedCount += item.invoicedCount || 0;
      if (!acc.dateObj && item.dateObj) acc.dateObj = item.dateObj;
      return acc;
    };

    return Array.from(allDates).map((date) => {
      const bucket = {
        date,
        dateObj: null,
        salesAmount: 0,
        costPrice: 0,
        productCost: 0,
        profit: 0,
        subtotal: 0,
        discountAmount: 0,
        shippingCost: 0,
        discountPercentage: 0,
        salesCount: 0,
        productsCount: 0,
        invoicedAmount: 0,
        invoicedCount: 0,
      };
      accumulate(bucket, ventasMercadoPago.find(v => v.date === date));
      accumulate(bucket, ventasPedidos.find(v => v.date === date));
      accumulate(bucket, ventasFisicas.find(v => v.date === date));
      return bucket;
    }).sort((a, b) => {
      const at = a.dateObj instanceof Date ? a.dateObj.getTime() : 0;
      const bt = b.dateObj instanceof Date ? b.dateObj.getTime() : 0;
      return at - bt;
    });
  }, [ventasMercadoPago, ventasPedidos, ventasFisicas]);

  const ventasConsignacion = useMemo(() => {
    return ventasFisicas
      .map((day) => {
        const pm = day.paymentMethods?.consignacion;
        const hasSplit = day.consignmentAmount != null || day.consignmentUnpaidAmount != null;
        const salesAmount = hasSplit ? (Number(day.consignmentAmount) || 0) : (Number(pm?.amount) || 0);
        const unpaidAmount = hasSplit ? (Number(day.consignmentUnpaidAmount) || 0) : salesAmount;
        const salesCount = hasSplit ? (Number(day.consignmentCount) || 0) : (Number(pm?.count) || 0);
        const productsCount = hasSplit ? (Number(day.consignmentProducts) || 0) : (Number(pm?.products) || 0);
        const unpaidCount = Number(day.consignmentUnpaidCount) || (unpaidAmount > 0 ? salesCount : 0);
        const hasConsignmentInvoiceSplit = day.consignmentInvoicedAmount != null;
        const otherPaymentAmount = ['efectivo', 'transferencia', 'debito', 'credito']
          .reduce((sum, method) => sum + (Number(day.paymentMethods?.[method]?.amount) || 0), 0);
        const invoicedAmount = hasConsignmentInvoiceSplit
          ? (Number(day.consignmentInvoicedAmount) || 0)
          : (otherPaymentAmount === 0 ? (Number(day.invoicedAmount) || 0) : 0);
        const invoicedCount = hasConsignmentInvoiceSplit
          ? (Number(day.consignmentInvoicedCount) || 0)
          : (otherPaymentAmount === 0 ? (Number(day.invoicedCount) || 0) : 0);
        return {
          date: day.date,
          dateObj: day.dateObj,
          salesAmount,
          unpaidAmount,
          unpaidCount,
          costPrice: Number(day.consignmentCost) || 0,
          productCost: Number(day.consignmentCost) || 0,
          profit: Number(day.consignmentProfit) || 0,
          subtotal: salesAmount,
          discountAmount: 0,
          shippingCost: 0,
          discountPercentage: 0,
          salesCount,
          productsCount,
          invoicedAmount,
          invoicedCount,
        };
      })
      .filter((row) => (row.salesAmount || 0) > 0 || (row.unpaidAmount || 0) > 0 || (row.invoicedAmount || 0) > 0);
  }, [ventasFisicas]);

  const totalsTodas = useMemo(() => calculateTotals(ventasTodas), [ventasTodas, calculateTotals]);

  const dailyAverageMercadoPago = useMemo(() => ({
    salesAmount: calculateDailyAverage(totalsMercadoPago.salesAmount),
    costPrice: calculateDailyAverage(totalsMercadoPago.costPrice),
    profit: calculateDailyAverage(totalsMercadoPago.profit),
  }), [totalsMercadoPago, calculateDailyAverage]);

  const dailyAveragePedidos = useMemo(() => ({
    salesAmount: calculateDailyAverage(totalsPedidos.salesAmount),
    costPrice: calculateDailyAverage(totalsPedidos.costPrice),
    profit: calculateDailyAverage(totalsPedidos.profit),
  }), [totalsPedidos, calculateDailyAverage]);

  const dailyAverageFisicas = useMemo(() => ({
    salesAmount: calculateDailyAverage(totalsFisicas.salesAmount),
    costPrice: calculateDailyAverage(totalsFisicas.costPrice),
    profit: calculateDailyAverage(totalsFisicas.profit),
  }), [totalsFisicas, calculateDailyAverage]);

  const monthlyAverageMercadoPago = useMemo(() => ({
    salesAmount: calculateMonthlyAverage(totalsMercadoPago.salesAmount),
    costPrice: calculateMonthlyAverage(totalsMercadoPago.costPrice),
    profit: calculateMonthlyAverage(totalsMercadoPago.profit),
  }), [totalsMercadoPago, calculateMonthlyAverage]);

  const monthlyAveragePedidos = useMemo(() => ({
    salesAmount: calculateMonthlyAverage(totalsPedidos.salesAmount),
    costPrice: calculateMonthlyAverage(totalsPedidos.costPrice),
    profit: calculateMonthlyAverage(totalsPedidos.profit),
  }), [totalsPedidos, calculateMonthlyAverage]);

  const monthlyAverageFisicas = useMemo(() => ({
    salesAmount: calculateMonthlyAverage(totalsFisicas.salesAmount),
    costPrice: calculateMonthlyAverage(totalsFisicas.costPrice),
    profit: calculateMonthlyAverage(totalsFisicas.profit),
  }), [totalsFisicas, calculateMonthlyAverage]);

  const dailyAverageTodas = useMemo(() => ({
    salesAmount: calculateDailyAverage(totalsTodas.salesAmount),
    costPrice: calculateDailyAverage(totalsTodas.costPrice),
    profit: calculateDailyAverage(totalsTodas.profit),
  }), [totalsTodas, calculateDailyAverage]);

  const monthlyAverageTodas = useMemo(() => ({
    salesAmount: calculateMonthlyAverage(totalsTodas.salesAmount),
    costPrice: calculateMonthlyAverage(totalsTodas.costPrice),
    profit: calculateMonthlyAverage(totalsTodas.profit),
  }), [totalsTodas, calculateMonthlyAverage]);

  const totalsConsignacion = useMemo(
    () => calculateTotals(ventasConsignacion),
    [ventasConsignacion, calculateTotals]
  );

  const dailyAverageConsignacion = useMemo(() => ({
    salesAmount: calculateDailyAverage(totalsConsignacion.salesAmount),
    costPrice: calculateDailyAverage(totalsConsignacion.costPrice),
    profit: calculateDailyAverage(totalsConsignacion.profit),
  }), [totalsConsignacion, calculateDailyAverage]);

  const monthlyAverageConsignacion = useMemo(() => ({
    salesAmount: calculateMonthlyAverage(totalsConsignacion.salesAmount),
    costPrice: calculateMonthlyAverage(totalsConsignacion.costPrice),
    profit: calculateMonthlyAverage(totalsConsignacion.profit),
  }), [totalsConsignacion, calculateMonthlyAverage]);

  const totalMonthsInRange = getTotalMonthsInRange();

  const currentChannel = useMemo(() => {
    if (chartCarouselIndex === 1) {
      return {
        data: ventasFisicas,
        title: 'Ventas Físicas',
        totals: totalsFisicas,
        dailyAverage: dailyAverageFisicas,
        monthlyAverage: monthlyAverageFisicas,
      };
    }
    if (chartCarouselIndex === 2) {
      return {
        data: ventasMercadoPago,
        title: 'Ventas MercadoPago',
        totals: totalsMercadoPago,
        dailyAverage: dailyAverageMercadoPago,
        monthlyAverage: monthlyAverageMercadoPago,
      };
    }
    if (chartCarouselIndex === 3) {
      return {
        data: ventasPedidos,
        title: 'Ventas Pedidos',
        totals: totalsPedidos,
        dailyAverage: dailyAveragePedidos,
        monthlyAverage: monthlyAveragePedidos,
      };
    }
    if (chartCarouselIndex === 4) {
      return {
        data: ventasConsignacion,
        title: 'Consignación',
        totals: totalsConsignacion,
        dailyAverage: dailyAverageConsignacion,
        monthlyAverage: monthlyAverageConsignacion,
      };
    }
    return {
      data: ventasTodas,
      title: 'Todas las Ventas',
      totals: totalsTodas,
      dailyAverage: dailyAverageTodas,
      monthlyAverage: monthlyAverageTodas,
    };
  }, [
    chartCarouselIndex,
    ventasFisicas, ventasMercadoPago, ventasPedidos, ventasTodas, ventasConsignacion,
    totalsFisicas, totalsMercadoPago, totalsPedidos, totalsTodas, totalsConsignacion,
    dailyAverageFisicas, dailyAverageMercadoPago, dailyAveragePedidos, dailyAverageTodas, dailyAverageConsignacion,
    monthlyAverageFisicas, monthlyAverageMercadoPago, monthlyAveragePedidos, monthlyAverageTodas, monthlyAverageConsignacion,
  ]);

  const calculateGeneralTotals = useMemo(() => {
    return {
      salesAmount: totalsMercadoPago.salesAmount + totalsPedidos.salesAmount + totalsFisicas.salesAmount,
      costPrice: totalsMercadoPago.costPrice + totalsPedidos.costPrice + totalsFisicas.costPrice,
      profit: totalsMercadoPago.profit + totalsPedidos.profit + totalsFisicas.profit,
      discountAmount: totalsMercadoPago.discountAmount + totalsPedidos.discountAmount + totalsFisicas.discountAmount,
      shippingCost: totalsMercadoPago.shippingCost + totalsPedidos.shippingCost + totalsFisicas.shippingCost,
    };
  }, [totalsMercadoPago, totalsPedidos, totalsFisicas]);

  const channelComparison = useMemo(() => [
    {
      name: 'MercadoPago',
      channel: 'MercadoPago',
      salesAmount: totalsMercadoPago.salesAmount,
      salesCount: totalsMercadoPago.salesCount ?? 0,
      profit: totalsMercadoPago.profit,
      marginPercentage: totalsMercadoPago.salesAmount > 0 ? (totalsMercadoPago.profit / totalsMercadoPago.salesAmount * 100) : 0,
      sharePercentage: calculateGeneralTotals.salesAmount > 0 ? (totalsMercadoPago.salesAmount / calculateGeneralTotals.salesAmount * 100) : 0,
      color: '#00a8ff'
    },
    {
      name: 'Pedidos',
      channel: 'Pedidos',
      salesAmount: totalsPedidos.salesAmount,
      salesCount: totalsPedidos.salesCount ?? 0,
      profit: totalsPedidos.profit,
      marginPercentage: totalsPedidos.salesAmount > 0 ? (totalsPedidos.profit / totalsPedidos.salesAmount * 100) : 0,
      sharePercentage: calculateGeneralTotals.salesAmount > 0 ? (totalsPedidos.salesAmount / calculateGeneralTotals.salesAmount * 100) : 0,
      color: '#ff9f43'
    },
    {
      name: 'Físicas',
      channel: 'Físicas',
      salesAmount: totalsFisicas.salesAmount,
      salesCount: totalsFisicas.salesCount ?? 0,
      profit: totalsFisicas.profit,
      marginPercentage: totalsFisicas.salesAmount > 0 ? (totalsFisicas.profit / totalsFisicas.salesAmount * 100) : 0,
      sharePercentage: calculateGeneralTotals.salesAmount > 0 ? (totalsFisicas.salesAmount / calculateGeneralTotals.salesAmount * 100) : 0,
      color: '#10ac84'
    }
  ], [totalsMercadoPago, totalsPedidos, totalsFisicas, calculateGeneralTotals]);

  const combineAllSalesData = useMemo(() => {
    const allDates = new Set([
      ...ventasMercadoPago.map(v => v.date),
      ...ventasPedidos.map(v => v.date),
      ...ventasFisicas.map(v => v.date)
    ]);

    return Array.from(allDates).sort().map(date => {
      const mpData = ventasMercadoPago.find(v => v.date === date) || { salesAmount: 0, costPrice: 0, profit: 0, salesCount: 0, productsCount: 0 };
      const pedData = ventasPedidos.find(v => v.date === date) || { salesAmount: 0, costPrice: 0, profit: 0, salesCount: 0, productsCount: 0 };
      const fisData = ventasFisicas.find(v => v.date === date) || { salesAmount: 0, costPrice: 0, profit: 0, salesCount: 0, productsCount: 0 };

      return {
        date,
        mercadoPago: mpData.salesAmount,
        pedidos: pedData.salesAmount,
        fisicas: fisData.salesAmount,
        total: mpData.salesAmount + pedData.salesAmount + fisData.salesAmount,
        totalProfit: mpData.profit + pedData.profit + fisData.profit,
        operationsCount: (mpData.salesCount || 0) + (pedData.salesCount || 0) + (fisData.salesCount || 0),
        productsCount: (mpData.productsCount || 0) + (pedData.productsCount || 0) + (fisData.productsCount || 0)
      };
    });
  }, [ventasMercadoPago, ventasPedidos, ventasFisicas]);

  // Totales de facturación (todos los canales: MercadoPago, Pedidos, Físicas)
  const totalInvoicedAll = useMemo(() =>
    (totalsMercadoPago.invoicedAmount || 0) + (totalsPedidos.invoicedAmount || 0) + (totalsFisicas.invoicedAmount || 0),
    [totalsMercadoPago, totalsPedidos, totalsFisicas]
  );
  const totalInvoicedCountAll = useMemo(() =>
    (totalsMercadoPago.invoicedCount || 0) + (totalsPedidos.invoicedCount || 0) + (totalsFisicas.invoicedCount || 0),
    [totalsMercadoPago, totalsPedidos, totalsFisicas]
  );

  // Datos combinados de facturación por día (para gráfico por canal)
  const combineInvoicedData = useMemo(() => {
    const allDates = new Set([
      ...ventasMercadoPago.filter(v => (v.invoicedAmount || 0) > 0).map(v => v.date),
      ...ventasPedidos.filter(v => (v.invoicedAmount || 0) > 0).map(v => v.date),
      ...ventasFisicas.filter(v => (v.invoicedAmount || 0) > 0).map(v => v.date)
    ]);
    return Array.from(allDates).sort().map(date => {
      const mp = ventasMercadoPago.find(v => v.date === date) || {};
      const ped = ventasPedidos.find(v => v.date === date) || {};
      const fis = ventasFisicas.find(v => v.date === date) || {};
      const mercadoPago = mp.invoicedAmount || 0;
      const pedidos = ped.invoicedAmount || 0;
      const fisicas = fis.invoicedAmount || 0;
      return {
        date,
        mercadoPago,
        pedidos,
        fisicas,
        total: mercadoPago + pedidos + fisicas
      };
    });
  }, [ventasMercadoPago, ventasPedidos, ventasFisicas]);

  // Tooltip específico para gráficos de métodos de pago (ventas físicas)
  const PaymentMethodsTooltip = ({ active, payload, metric = 'amount' }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0]?.payload || {};
    const label = data.name || payload[0].name || '';
    const amount = data.value ?? payload[0].value ?? 0;
    const operations = data.operations ?? data.count ?? 0;
    const products = data.products ?? 0;
    const isByAmount = metric === 'amount';
    const rawSliceColor = data.color || payload[0]?.color || payload[0]?.payload?.fill;
    const sliceColor = rawSliceColor && typeof rawSliceColor === 'string' && !rawSliceColor.startsWith('url(')
      ? rawSliceColor
      : null;

    return (
      <div
        className="wsc-custom-tooltip wsc-tooltip--narrow"
        style={sliceColor ? { '--wsc-series-color': sliceColor } : undefined}
      >
        <p className="wsc-tooltip-title">
          {sliceColor && <span className="wsc-tooltip-swatch" aria-hidden="true" />}
          <span className={`wsc-tooltip-title-label${sliceColor ? ' wsc-tooltip-title-label--series' : ''}`}>
            {label}
          </span>
        </p>
        <p className={`wsc-tooltip-row wsc-tooltip-row--flush wsc-tooltip-row--secondary${!isByAmount ? ' wsc-tooltip-row--primary' : ''}`}>
          <span className="wsc-tooltip-strong">{operations}</span> operaciones
        </p>
        <p className="wsc-tooltip-row wsc-tooltip-row--flush wsc-tooltip-row--products">
          <span className="wsc-tooltip-strong">{products}</span> productos vendidos
        </p>
        <p className={`wsc-tooltip-row wsc-tooltip-row--flush wsc-tooltip-row--money${isByAmount ? ' wsc-tooltip-row--primary' : ''}`}>
          <span className="wsc-tooltip-strong">$ {formatNumber(amount)}</span> en total
        </p>
      </div>
    );
  };

  // Tooltip para distribución por canal (pastel)
  const ChannelDistributionTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0]?.payload || {};
    const name = data.channel || payload[0].name || '';
    const sales = data.salesAmount ?? payload[0].value ?? 0;
    const operations = data.salesCount ?? 0;

    return (
      <div
        className="wsc-custom-tooltip wsc-tooltip--narrow"
        style={data.color ? { '--wsc-series-color': data.color } : undefined}
      >
        <p className="wsc-tooltip-channel-title wsc-tooltip-strong">
          {data.color && <span className="wsc-tooltip-swatch" aria-hidden="true" />}
          {name}
        </p>
        <p className="wsc-tooltip-row wsc-tooltip-row--flush wsc-tooltip-row--secondary">
          <span className="wsc-tooltip-strong">{operations}</span> operaciones
        </p>
        <p className="wsc-tooltip-row wsc-tooltip-row--flush wsc-tooltip-row--money">
          Monto: <span className="wsc-tooltip-strong">$ {formatNumber(sales)}</span>
        </p>
      </div>
    );
  };

  // Nueva función para renderizar análisis de métodos de pago solo de ventas físicas
  const renderPaymentMethodsAnalysis = () => {
    const totalSales = Object.values(paymentMethodsAnalysis).reduce((sum, method) => sum + method.totalAmount, 0);
    
    if (totalSales === 0) return null;

    // Total de ventas únicas (cada día puede tener varias ventas; salesCount por día)
    const totalUniqueSales = ventasFisicas.reduce((sum, day) => sum + (day.salesCount || 0), 0);

    const paymentMethodLabels = {
      efectivo: 'Efectivo',
      transferencia: 'Transferencia',
      debito: 'Débito',
      credito: 'Crédito',
      consignacion: 'Consignación (no cobrada)'
    };

    const paymentMethodsData = Object.entries(paymentMethodsAnalysis)
      .filter(([method, data]) => data.totalAmount > 0)
      .map(([method, data]) => ({
        methodKey: method,
        name: paymentMethodLabels[method] || (method.charAt(0).toUpperCase() + method.slice(1)),
        value: data.totalAmount,
        count: data.totalCount,
        products: data.totalProducts,
        percentage: (data.totalAmount / totalSales * 100).toFixed(1),
        color: {
          efectivo: '#2ecc71',
          transferencia: '#3498db',
          debito: '#e74c3c',
          credito: '#f39c12',
          consignacion: '#8b5cf6'
        }[method]
      }));

    // Crear datos para el gráfico Digital vs Efectivo
    const digitalTotal = paymentMethodsAnalysis.transferencia.totalAmount + 
                        paymentMethodsAnalysis.debito.totalAmount + 
                        paymentMethodsAnalysis.credito.totalAmount;
    const cashTotal = paymentMethodsAnalysis.efectivo.totalAmount;

    const digitalOps = paymentMethodsAnalysis.transferencia.totalCount +
                       paymentMethodsAnalysis.debito.totalCount +
                       paymentMethodsAnalysis.credito.totalCount;
    const cashOps = paymentMethodsAnalysis.efectivo.totalCount;

    const digitalProducts = paymentMethodsAnalysis.transferencia.totalProducts +
                            paymentMethodsAnalysis.debito.totalProducts +
                            paymentMethodsAnalysis.credito.totalProducts;
    const cashProducts = paymentMethodsAnalysis.efectivo.totalProducts;

    const consignmentTotal = paymentMethodsAnalysis.consignacion?.totalAmount || 0;
    const consignmentOps = paymentMethodsAnalysis.consignacion?.totalCount || 0;
    const consignmentProducts = paymentMethodsAnalysis.consignacion?.totalProducts || 0;

    const digitalVsCashData = [
      {
        name: 'Efectivo',
        value: cashTotal,
        operations: cashOps,
        products: cashProducts,
        percentage: totalSales > 0 ? (cashTotal / totalSales * 100).toFixed(1) : 0,
        color: '#2ecc71'
      },
      {
        name: 'Canales Digitales',
        value: digitalTotal,
        operations: digitalOps,
        products: digitalProducts,
        percentage: totalSales > 0 ? (digitalTotal / totalSales * 100).toFixed(1) : 0,
        color: '#3498db'
      },
      {
        name: 'Consignación (no cobrada)',
        value: consignmentTotal,
        operations: consignmentOps,
        products: consignmentProducts,
        percentage: totalSales > 0 ? (consignmentTotal / totalSales * 100).toFixed(1) : 0,
        color: '#8b5cf6'
      }
    ].filter(item => item.value > 0);

    // Insights derivados (se calculan una sola vez por render)
    const paymentInsights = paymentMethodsData.length > 0 ? {
      mostUsed: paymentMethodsData.reduce((prev, cur) => (prev.count > cur.count) ? prev : cur),
      highestAmount: paymentMethodsData.reduce((prev, cur) => (prev.value > cur.value) ? prev : cur),
      highestAverage: paymentMethodsData.reduce(
        (prev, cur) => ((prev.value / prev.count) > (cur.value / cur.count)) ? prev : cur
      ),
    } : null;
    const digitalEntry = digitalVsCashData.find(d => d.name === 'Canales Digitales');
    const digitalPct = digitalEntry ? parseFloat(digitalEntry.percentage || 0) : null;

    return (
      <div className="payment-methods-analysis">
        <h3>💳 Análisis de Métodos de Pago - Ventas Físicas</h3>
        {totalUniqueSales > 0 && (
          <p className="payment-methods-totalSales">
            Total de ventas en el período: <strong>{totalUniqueSales}</strong> (cada venta utiliza un único método de pago; la consignación se contabiliza como no cobrada)
          </p>
        )}
        <div className="payment-methods-summary">
          <div className="payment-methods-grid">
            {paymentMethodsData.map((method, index) => (
              <div key={index} className={`payment-method-card payment-method-card--${method.methodKey}`}>
                <h5>{method.name}</h5>
                <div className="payment-stat">
                  <span className="payment-stat-label">Monto Total:</span>
                  <span className="payment-stat-value">$ {formatNumber(method.value)}</span>
                </div>
                <div className="payment-stat">
                  <span className="payment-stat-label">Ventas que usaron este método:</span>
                  <span className="payment-stat-value">{method.count}</span>
                </div>
                <div className="payment-stat">
                  <span className="payment-stat-label">% del Total:</span>
                  <span className="payment-stat-value">{method.percentage}%</span>
                </div>
                <div className="payment-stat">
                  <span className="payment-stat-label">Promedio por venta:</span>
                  <span className="payment-stat-value">
                    $ {method.count > 0 ? formatNumber(method.value / method.count) : '0,00'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="payment-pie-metric-switcher" role="group" aria-label="Unidad de las donas">
          <span className="wallStreetChart-granularityLabel">Ver por:</span>
          <div className="wallStreetChart-granularityBtns">
            {[
              { value: 'amount', label: 'Monto' },
              { value: 'count', label: 'Cantidad' },
            ].map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={`wallStreetChart-granularityBtn${paymentPieMetric === value ? ' wallStreetChart-granularityBtn--active' : ''}`}
                onClick={() => setPaymentPieMetric(value)}
                aria-pressed={paymentPieMetric === value}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="payment-methods-charts">
          <div className="chart-container">
            <h4>
              Distribución Detallada por Método
              <span className="chart-container-subtitle">
                {paymentPieMetric === 'amount' ? 'por monto vendido' : 'por cantidad de operaciones'}
              </span>
            </h4>
            <ResponsiveContainer width="100%" height={320}>
              <PieChart margin={{ top: 24, right: 12, bottom: 24, left: 12 }}>
                <defs>
                  {renderColorGradients(paymentMethodsData.map((e) => e.color), 'pie')}
                </defs>
                <Pie
                  data={paymentMethodsData}
                  cx="50%"
                  cy="45%"
                  innerRadius={45}
                  outerRadius={65}
                  fill="#8884d8"
                  stroke="none"
                  dataKey={paymentPieMetric === 'amount' ? 'value' : 'count'}
                  label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                  labelLine={false}
                >
                  {paymentMethodsData.map((entry, index) => (
                    <Cell key={`cell-${index}`} stroke="none" fill={gradientFill(entry.color, 'pie')} />
                  ))}
                </Pie>
                <Tooltip content={<PaymentMethodsTooltip metric={paymentPieMetric} />} {...wallStreetTooltipProps} />
                <Legend
                  verticalAlign="bottom"
                  height={60}
                  wrapperClassName="wallStreetChart-rechartsLegend"
                  payload={paymentMethodsData.map((entry) => ({
                    value: entry.name,
                    type: 'square',
                    id: entry.name,
                    color: entry.color,
                  }))}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-container">
            <h4>
              Digital, efectivo y consignación
              <span className="chart-container-subtitle">
                {paymentPieMetric === 'amount' ? 'por monto vendido' : 'por cantidad de operaciones'}
              </span>
            </h4>
            <ResponsiveContainer width="100%" height={320}>
              <PieChart margin={{ top: 24, right: 12, bottom: 24, left: 12 }}>
                <defs>
                  {renderColorGradients(digitalVsCashData.map((e) => e.color), 'pie')}
                </defs>
                <Pie
                  data={digitalVsCashData}
                  cx="50%"
                  cy="45%"
                  innerRadius={45}
                  outerRadius={65}
                  fill="#8884d8"
                  stroke="none"
                  dataKey={paymentPieMetric === 'amount' ? 'value' : 'operations'}
                  label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                  labelLine={false}
                >
                  {digitalVsCashData.map((entry, index) => (
                    <Cell key={`cell-${index}`} stroke="none" fill={gradientFill(entry.color, 'pie')} />
                  ))}
                </Pie>
                <Tooltip content={<PaymentMethodsTooltip metric={paymentPieMetric} />} {...wallStreetTooltipProps} />
                <Legend
                  verticalAlign="bottom"
                  height={60}
                  wrapperClassName="wallStreetChart-rechartsLegend"
                  payload={digitalVsCashData.map((entry) => ({
                    value: entry.name,
                    type: 'square',
                    id: entry.name,
                    color: entry.color,
                  }))}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="payment-insights">
          <h4>💡 Insights de Métodos de Pago (Solo Ventas Físicas)</h4>
          <div className="insights-grid">
            {!paymentInsights ? (
              <InsightCard icon="📊" title="Sin Datos">
                <p>No hay información de métodos de pago en las ventas físicas del período seleccionado.</p>
              </InsightCard>
            ) : (
              <>
                <InsightCard icon="🔥" title="Método Más Usado">
                  <p>
                    <AccentText color={paymentInsights.mostUsed.color}>{paymentInsights.mostUsed.name}</AccentText>{' '}
                    con {paymentInsights.mostUsed.count} transacciones ({paymentInsights.mostUsed.percentage}% del total)
                  </p>
                </InsightCard>
                <InsightCard icon="💰" title="Mayor Volumen">
                  <p>
                    <AccentText color={paymentInsights.highestAmount.color}>{paymentInsights.highestAmount.name}</AccentText>{' '}
                    genera $ {formatNumber(paymentInsights.highestAmount.value)} en ventas
                  </p>
                </InsightCard>
                <InsightCard icon="🎯" title="Mayor Ticket Promedio">
                  <p>
                    <AccentText color={paymentInsights.highestAverage.color}>{paymentInsights.highestAverage.name}</AccentText>{' '}
                    con $ {formatNumber(paymentInsights.highestAverage.value / paymentInsights.highestAverage.count)} promedio
                  </p>
                </InsightCard>
                <InsightCard icon="📱" title="Adopción Digital">
                  <p>
                    {digitalPct !== null && digitalVsCashData.length === 2 ? (
                      <>
                        Los canales digitales representan el <strong>{digitalEntry.percentage}%</strong> de las ventas físicas.
                        {digitalPct > INSIGHT_THRESHOLDS.DIGITAL_ADOPTION_HIGH
                          ? ' 📈 ¡Excelente adopción digital!'
                          : ' 💡 Oportunidad de impulsar métodos digitales.'}
                      </>
                    ) : (
                      'Análisis disponible cuando hay tanto pagos digitales como en efectivo.'
                    )}
                  </p>
                </InsightCard>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTotalsHeader = () => {
    const { title, totals, dailyAverage, monthlyAverage } = currentChannel;
    const numberOfDays = getTotalDaysInRange();
    const dailyAvgOperations = numberOfDays > 0 ? (totals.salesCount ?? 0) / numberOfDays : 0;
    const dailyAvgProducts = numberOfDays > 0 ? (totals.productsCount ?? 0) / numberOfDays : 0;

    return (
      <div className="wallStreetChart-statsHeader" role="region" aria-label="Totales del canal seleccionado">
        <div className="wallStreetChart-statsHeaderTitle">
          <span className="wallStreetChart-statsHeaderChannel">{title}</span>
        </div>
        <div className="totals-summary">
          <div className="totals-summary-card totals-summary-card--ops">
            <span className="totals-summary-label">Operaciones</span>
            <span className="totals-summary-value">{totals.salesCount ?? 0}</span>
            <div className="totals-summary-details">
              <div className="totals-summary-row">
                <span className="totals-summary-sublabel">Productos vendidos</span>
                <span className="totals-summary-subvalue">{totals.productsCount ?? 0}</span>
              </div>
              <div className="totals-summary-row">
                <span className="totals-summary-sublabel">Promedio diario operaciones</span>
                <span className="totals-summary-subvalue">{dailyAvgOperations % 1 === 0 ? dailyAvgOperations : dailyAvgOperations.toFixed(1)}</span>
              </div>
              <div className="totals-summary-row">
                <span className="totals-summary-sublabel">Promedio diario productos</span>
                <span className="totals-summary-subvalue">{dailyAvgProducts % 1 === 0 ? dailyAvgProducts : dailyAvgProducts.toFixed(1)}</span>
              </div>
            </div>
          </div>
          <div className="totals-summary-card totals-summary-card--sales">
            <span className="totals-summary-label">Total Ventas</span>
            <span className="totals-summary-value">$ {formatNumber(totals.salesAmount)}</span>
            <div className="totals-summary-details">
              <div className="totals-summary-row">
                <span className="totals-summary-sublabel">Promedio diario</span>
                <span className="totals-summary-subvalue">$ {formatNumber(dailyAverage.salesAmount)}</span>
              </div>
              {totalMonthsInRange > 1 && (
                <div className="totals-summary-row">
                  <span className="totals-summary-sublabel">Promedio mensual</span>
                  <span className="totals-summary-subvalue">$ {formatNumber(monthlyAverage?.salesAmount ?? 0)}</span>
                </div>
              )}
            </div>
          </div>
          <div className="totals-summary-card totals-summary-card--cost">
            <span className="totals-summary-label">Total Costo</span>
            <span className="totals-summary-value">$ {formatNumber(totals.costPrice)}</span>
            <div className="totals-summary-details">
              {totals.shippingCost > 0 && (
                <>
                  <div className="totals-summary-row">
                    <span className="totals-summary-sublabel">Costo de productos</span>
                    <span className="totals-summary-subvalue">$ {formatNumber(totals.productCost ?? 0)}</span>
                  </div>
                  <div className="totals-summary-row">
                    <span className="totals-summary-sublabel">Costo de envíos</span>
                    <span className="totals-summary-subvalue">$ {formatNumber(totals.shippingCost)}</span>
                  </div>
                </>
              )}
              <div className="totals-summary-row">
                <span className="totals-summary-sublabel">Promedio diario</span>
                <span className="totals-summary-subvalue">$ {formatNumber(dailyAverage.costPrice)}</span>
              </div>
              {totalMonthsInRange > 1 && (
                <div className="totals-summary-row">
                  <span className="totals-summary-sublabel">Promedio mensual</span>
                  <span className="totals-summary-subvalue">$ {formatNumber(monthlyAverage?.costPrice ?? 0)}</span>
                </div>
              )}
            </div>
          </div>
          <div className="totals-summary-card totals-summary-card--profit">
            <span className="totals-summary-label">Total Ganancia</span>
            <span className="totals-summary-value">$ {formatNumber(totals.profit)}</span>
            <div className="totals-summary-details">
              <div className="totals-summary-row">
                <span className="totals-summary-sublabel">Promedio diario</span>
                <span className="totals-summary-subvalue">$ {formatNumber(dailyAverage.profit)}</span>
              </div>
              {totalMonthsInRange > 1 && (
                <div className="totals-summary-row">
                  <span className="totals-summary-sublabel">Promedio mensual</span>
                  <span className="totals-summary-subvalue">$ {formatNumber(monthlyAverage?.profit ?? 0)}</span>
                </div>
              )}
            </div>
          </div>
          {totals.discountAmount > 0 && (
            <div className="totals-summary-card totals-summary-card--discount">
              <span className="totals-summary-label">Total Descuentos</span>
              <span className="totals-summary-value">$ {formatNumber(totals.discountAmount)}</span>
              <div className="totals-summary-details">
                <div className="totals-summary-row">
                  <span className="totals-summary-sublabel">Subtotal antes de desc.</span>
                  <span className="totals-summary-subvalue">$ {formatNumber(totals.subtotal)}</span>
                </div>
              </div>
            </div>
          )}
          {totals.shippingCost > 0 && (
            <div className="totals-summary-card totals-summary-card--shipping">
              <span className="totals-summary-label">Total Envíos</span>
              <span className="totals-summary-value">$ {formatNumber(totals.shippingCost)}</span>
              <div className="totals-summary-details">
                <div className="totals-summary-row">
                  <span className="totals-summary-sublabel">Concepto</span>
                  <span className="totals-summary-subvalue totals-summary-subvalue--text">Cobrado al cliente y pagado al transporte (no es ganancia, se imputa al costo)</span>
                </div>
              </div>
            </div>
          )}
          {(totals.invoicedAmount > 0 || totals.invoicedCount > 0) && (
            <div className="totals-summary-card totals-summary-card--invoiced">
              <span className="totals-summary-label">Facturado (AFIP)</span>
              <span className="totals-summary-value">$ {formatNumber(totals.invoicedAmount || 0)}</span>
              <div className="totals-summary-details">
                <div className="totals-summary-row">
                  <span className="totals-summary-sublabel">Comprobantes emitidos</span>
                  <span className="totals-summary-subvalue">{totals.invoicedCount || 0}</span>
                </div>
              </div>
            </div>
          )}
          {chartCarouselIndex === 4 && (
            <div className="totals-summary-card totals-summary-card--unpaid">
              <span className="totals-summary-label">No cobrada</span>
              <span className="totals-summary-value">$ {formatNumber(totals.unpaidAmount || 0)}</span>
              <div className="totals-summary-details">
                <div className="totals-summary-row">
                  <span className="totals-summary-sublabel">Operaciones pendientes</span>
                  <span className="totals-summary-subvalue">{totals.unpaidCount || 0}</span>
                </div>
                <div className="totals-summary-row">
                  <span className="totals-summary-sublabel">Ya cobrada</span>
                  <span className="totals-summary-subvalue">$ {formatNumber(Math.max(0, (totals.salesAmount || 0) - (totals.unpaidAmount || 0)))}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderChart = (data) => {
    const chartData = aggregateByGranularity(data, chartGranularity);
    const barSize = chartGranularity === 'day' ? 20 : chartGranularity === 'week' ? 26 : 36;
    const invoicedBarSize = Math.max(barSize - 4, 12);
    const isConsignmentView = chartCarouselIndex === 4;
    const hasInvoiced = chartData.some((d) => (d.invoicedAmount || 0) > 0);
    const hasUnpaidCandles = isConsignmentView && chartData.some((d) => (d.salesAmount || 0) > 0);
    const legendItems = [
      { key: 'salesAmount', label: isConsignmentView ? 'Total consignación' : 'Total Ventas', color: '#3b82f6' },
      { key: 'costPrice', label: 'Precio de Costo', color: '#ef4444' },
      { key: 'profit', label: 'Ganancia', color: '#10b981' },
      ...(hasInvoiced ? [{ key: 'invoicedAmount', label: 'Facturado (AFIP)', color: '#8b5cf6' }] : []),
      ...(hasUnpaidCandles ? [{ key: 'unpaidAmount', label: 'No cobrada (vela)', color: '#ef4444' }] : []),
    ];
    const renderChartLegend = () => (
      <div className="wsc-chart-legend" role="group" aria-label="Series del gráfico">
        {legendItems.map(({ key, label, color }) => {
          const isActive = !!activeLines[key];
          const isHidden = !!hiddenSeries[key];
          return (
            <span
              key={key}
              className={`wsc-chart-legend-chip ${isActive ? 'wsc-chart-legend-chip--active' : ''} ${isHidden ? 'wsc-chart-legend-chip--hidden' : ''}`}
              style={{ '--wsc-legend-color': color }}
            >
              <button
                type="button"
                className="wsc-chart-legend-toggle"
                onClick={() => handleLegendClick(key)}
                aria-pressed={isActive}
                disabled={isHidden}
                title={
                  isHidden
                    ? 'Serie oculta'
                    : isActive
                      ? 'Ocultar línea de tendencia'
                      : 'Mostrar línea de tendencia'
                }
              >
                <span className="wsc-chart-legend-dot" aria-hidden="true" />
                <span className="wsc-chart-legend-label">{label}</span>
              </button>
              <button
                type="button"
                className="wsc-chart-legend-remove"
                onClick={() => handleSeriesToggle(key)}
                aria-pressed={isHidden}
                aria-label={isHidden ? `Mostrar ${label} en el gráfico` : `Quitar ${label} del gráfico`}
                title={isHidden ? `Mostrar ${label}` : `Quitar ${label} del gráfico`}
              >
                {isHidden ? '+' : '×'}
              </button>
            </span>
          );
        })}
      </div>
    );
    return (
    <>
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart data={chartData}>
          <defs>
            <linearGradient id={`wscGradSales-${chartCarouselIndex}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9} />
              <stop offset="50%" stopColor="#3b82f6" stopOpacity={0.75} />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.55} />
            </linearGradient>
            <linearGradient id={`wscGradCost-${chartCarouselIndex}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ef4444" stopOpacity={0.9} />
              <stop offset="50%" stopColor="#ef4444" stopOpacity={0.72} />
              <stop offset="100%" stopColor="#ef4444" stopOpacity={0.5} />
            </linearGradient>
            <linearGradient id={`wscGradProfit-${chartCarouselIndex}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity={0.9} />
              <stop offset="50%" stopColor="#10b981" stopOpacity={0.75} />
              <stop offset="100%" stopColor="#10b981" stopOpacity={0.55} />
            </linearGradient>
            <linearGradient id={`wscGradInvoiced-${chartCarouselIndex}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.9} />
              <stop offset="50%" stopColor="#8b5cf6" stopOpacity={0.72} />
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.5} />
            </linearGradient>
            <linearGradient id={`wscGradSalesBar-${chartCarouselIndex}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity={1} />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.55} />
            </linearGradient>
            <linearGradient id={`wscGradCostBar-${chartCarouselIndex}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ef4444" stopOpacity={1} />
              <stop offset="100%" stopColor="#ef4444" stopOpacity={0.55} />
            </linearGradient>
            <linearGradient id={`wscGradProfitBar-${chartCarouselIndex}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
              <stop offset="100%" stopColor="#10b981" stopOpacity={0.55} />
            </linearGradient>
            <linearGradient id={`wscGradInvoicedBar-${chartCarouselIndex}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8b5cf6" stopOpacity={1} />
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.55} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip content={<CustomTooltip />} {...wallStreetTooltipProps} />
          <Legend wrapperClassName="wallStreetChart-rechartsLegend" content={renderChartLegend} />
          {chartType === 'bar' ? (
            <>
              {!isConsignmentView && !hiddenSeries.salesAmount && (
                <Bar dataKey="salesAmount" fill={`url(#wscGradSalesBar-${chartCarouselIndex})`} name="Total Ventas" barSize={barSize} />
              )}
              {isConsignmentView && !hiddenSeries.salesAmount && (
                <Bar dataKey="salesAmount" fill={`url(#wscGradSalesBar-${chartCarouselIndex})`} name="Total consignación" barSize={Math.max(barSize - 6, 10)} />
              )}
              {!hiddenSeries.costPrice && (
                <Bar dataKey="costPrice" fill={`url(#wscGradCostBar-${chartCarouselIndex})`} name="Precio de Costo" barSize={barSize} stackId="stack" />
              )}
              {!hiddenSeries.profit && (
                <Bar dataKey="profit" fill={`url(#wscGradProfitBar-${chartCarouselIndex})`} name="Ganancia" barSize={barSize} stackId="stack" />
              )}
              {hasInvoiced && !hiddenSeries.invoicedAmount && (
                <Bar dataKey="invoicedAmount" fill={`url(#wscGradInvoicedBar-${chartCarouselIndex})`} name="Facturado (AFIP)" barSize={invoicedBarSize} />
              )}
            </>
          ) : (
            <>
              {!hiddenSeries.salesAmount && (
                <Area
                  type="monotone"
                  dataKey="salesAmount"
                  name="Total Ventas"
                  stroke="none"
                  fill={`url(#wscGradSales-${chartCarouselIndex})`}
                  activeDot={false}
                />
              )}
              {!hiddenSeries.costPrice && (
                <Area
                  type="monotone"
                  dataKey="costPrice"
                  name="Precio de Costo"
                  stroke="none"
                  fill={`url(#wscGradCost-${chartCarouselIndex})`}
                  activeDot={false}
                />
              )}
              {!hiddenSeries.profit && (
                <Area
                  type="monotone"
                  dataKey="profit"
                  name="Ganancia"
                  stroke="none"
                  fill={`url(#wscGradProfit-${chartCarouselIndex})`}
                  activeDot={false}
                />
              )}
              {hasInvoiced && !hiddenSeries.invoicedAmount && (
                <Area
                  type="monotone"
                  dataKey="invoicedAmount"
                  name="Facturado (AFIP)"
                  stroke="none"
                  fill={`url(#wscGradInvoiced-${chartCarouselIndex})`}
                  activeDot={false}
                />
              )}
            </>
          )}
          {hasUnpaidCandles && !hiddenSeries.unpaidAmount && (
            <Bar
              dataKey="salesAmount"
              shape={<ConsignmentCandle />}
              name="No cobrada (vela)"
              barSize={Math.max(barSize + 10, 24)}
              legendType="none"
              isAnimationActive={false}
            />
          )}
          {activeLines.salesAmount && !hiddenSeries.salesAmount && (
            <Line
              type="monotone"
              dataKey="salesAmount"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 8 }}
              legendType="none"
            />
          )}
          {activeLines.costPrice && !hiddenSeries.costPrice && (
            <Line
              type="monotone"
              dataKey="costPrice"
              stroke="#ef4444"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 8 }}
              legendType="none"
            />
          )}
          {activeLines.profit && !hiddenSeries.profit && (
            <Line
              type="monotone"
              dataKey="profit"
              stroke="#10b981"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 8 }}
              legendType="none"
            />
          )}
          {hasInvoiced && activeLines.invoicedAmount && !hiddenSeries.invoicedAmount && (
            <Line
              type="monotone"
              dataKey="invoicedAmount"
              stroke="#8b5cf6"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 8 }}
              legendType="none"
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </>
  );
  };

  const renderCompleteAnalysis = () => {
    const generalMarginPercentage = calculateGeneralTotals.salesAmount > 0 ?
      (calculateGeneralTotals.profit / calculateGeneralTotals.salesAmount * 100) : 0;

    const bestChannel = channelComparison.reduce((prev, current) =>
      (prev.salesAmount > current.salesAmount) ? prev : current
    );

    const mostProfitableChannel = channelComparison.reduce((prev, current) =>
      (prev.marginPercentage > current.marginPercentage) ? prev : current
    );

    const totalDays = getTotalDaysInRange();
    const totalOperations =
      (totalsMercadoPago.salesCount || 0) +
      (totalsPedidos.salesCount || 0) +
      (totalsFisicas.salesCount || 0);
    const totalProducts =
      (totalsMercadoPago.productsCount || 0) +
      (totalsPedidos.productsCount || 0) +
      (totalsFisicas.productsCount || 0);
    const dailyAverageGeneral = totalDays > 0 ? calculateGeneralTotals.salesAmount / totalDays : 0;

    // Días con ventas efectivas (unión de fechas con monto > 0 entre los tres canales)
    const daysWithSales = new Set(
      [...ventasMercadoPago, ...ventasPedidos, ...ventasFisicas]
        .filter((d) => (d.salesAmount || 0) > 0)
        .map((d) => d.date)
    ).size;
    const daysWithoutSales = Math.max(0, totalDays - daysWithSales);
    const daysWithoutSalesRatio = totalDays > 0 ? daysWithoutSales / totalDays : 0;

    // Estado semántico para el margen general (drive del badge del indicador)
    let marginStatus = 'bad';
    let marginLabel = 'Mejorable';
    if (generalMarginPercentage > INSIGHT_THRESHOLDS.MARGIN_EXCELLENT) {
      marginStatus = 'good';
      marginLabel = 'Excelente';
    } else if (generalMarginPercentage > INSIGHT_THRESHOLDS.MARGIN_GOOD) {
      marginStatus = 'warn';
      marginLabel = 'Bueno';
    }

    // Recomendaciones dinámicas: se arman sólo cuando los datos lo justifican y se
    // limitan a las 4 más relevantes para no saturar la tarjeta.
    const dynamicRecommendations = [];
    if (generalMarginPercentage < INSIGHT_THRESHOLDS.MARGIN_GOOD) {
      dynamicRecommendations.push({
        icon: '⚠️',
        text: `Revisar estructura de costos: el margen actual (${generalMarginPercentage.toFixed(1)}%) está por debajo del umbral objetivo.`,
      });
    }
    if (bestChannel.channel !== mostProfitableChannel.channel) {
      dynamicRecommendations.push({
        icon: '💎',
        text: `Potenciar ${mostProfitableChannel.channel}: concentra el mejor margen (${mostProfitableChannel.marginPercentage.toFixed(1)}%) aunque no lidera en volumen.`,
      });
    }
    if (daysWithoutSalesRatio > INSIGHT_THRESHOLDS.DAYS_WITHOUT_SALES_ALERT) {
      dynamicRecommendations.push({
        icon: '📅',
        text: `Analizar los ${daysWithoutSales} días sin ventas (${(daysWithoutSalesRatio * 100).toFixed(0)}% del período) para detectar patrones.`,
      });
    }
    if (calculateGeneralTotals.discountAmount > calculateGeneralTotals.profit * INSIGHT_THRESHOLDS.DISCOUNT_VS_PROFIT_HEAVY) {
      dynamicRecommendations.push({
        icon: '🎫',
        text: 'Evaluar política de descuentos: su impacto sobre la rentabilidad es significativo.',
      });
    }

    return (
      <div className="complete-analysis">
        <h3>📊 Análisis Completo del Período</h3>

        {(totalInvoicedAll > 0 || calculateGeneralTotals.salesAmount > 0) && (
          <div className="analysis-section">
            <h4>🏛️ Indicadores de facturación AFIP</h4>
            {totalInvoicedAll === 0 ? (
              <div className="invoiced-empty-state" role="status">
                <p>No hay ventas facturadas en el período seleccionado. Las ventas con comprobante AFIP emitido aparecerán aquí.</p>
              </div>
            ) : (
            <>
            <p className="invoiced-section-desc">Montos facturados por canal (MercadoPago, Pedidos, Ventas físicas) — comprobantes emitidos en el período.</p>
            <div className="general-summary-grid">
              <div className="summary-card gradient-invoice">
                <h5>TOTAL FACTURADO</h5>
                <p className="main-value">$ {formatNumber(totalInvoicedAll)}</p>
                <p className="sub-value">Todos los canales · período seleccionado</p>
              </div>
              <div className="summary-card gradient-invoice">
                <h5>COMPROBANTES EMITIDOS</h5>
                <p className="main-value">{totalInvoicedCountAll}</p>
                <p className="sub-value">Facturas / tickets AFIP</p>
              </div>
              <div className="summary-card gradient-invoice">
                <h5>PROMEDIO DIARIO FACTURADO</h5>
                <p className="main-value">$ {formatNumber(calculateDailyAverage(totalInvoicedAll))}</p>
                <p className="sub-value">Por día en el rango</p>
              </div>
              <div className="summary-card gradient-invoice">
                <h5>% VENTAS TOTALES FACTURADAS</h5>
                <p className="main-value">
                  {calculateGeneralTotals.salesAmount > 0
                    ? ((totalInvoicedAll / calculateGeneralTotals.salesAmount) * 100).toFixed(1)
                    : '0'}%
                </p>
                <p className="sub-value">Del total de ventas (todos los canales)</p>
              </div>
            </div>
            <div className="invoiced-breakdown">
              <h5>Facturado por canal</h5>
              <div className="invoiced-breakdown-grid">
                {(totalsMercadoPago.invoicedAmount || 0) > 0 && (
                  <div className="invoiced-breakdown-card invoiced-breakdown-card--mercadopago">
                    <span className="invoiced-breakdown-label">MercadoPago</span>
                    <span className="invoiced-breakdown-value">$ {formatNumber(totalsMercadoPago.invoicedAmount)}</span>
                    <span className="invoiced-breakdown-count">{totalsMercadoPago.invoicedCount || 0} comprobantes</span>
                  </div>
                )}
                {(totalsPedidos.invoicedAmount || 0) > 0 && (
                  <div className="invoiced-breakdown-card invoiced-breakdown-card--pedidos">
                    <span className="invoiced-breakdown-label">Pedidos</span>
                    <span className="invoiced-breakdown-value">$ {formatNumber(totalsPedidos.invoicedAmount)}</span>
                    <span className="invoiced-breakdown-count">{totalsPedidos.invoicedCount || 0} comprobantes</span>
                  </div>
                )}
                {(totalsFisicas.invoicedAmount || 0) > 0 && (
                  <div className="invoiced-breakdown-card invoiced-breakdown-card--fisicas">
                    <span className="invoiced-breakdown-label">Ventas físicas</span>
                    <span className="invoiced-breakdown-value">$ {formatNumber(totalsFisicas.invoicedAmount)}</span>
                    <span className="invoiced-breakdown-count">{totalsFisicas.invoicedCount || 0} comprobantes</span>
                  </div>
                )}
              </div>
            </div>
            <div className="invoiced-chart-wrap">
              <h5>Monto facturado por día (por canal)</h5>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={combineInvoicedData}
                  margin={{ top: 12, right: 12, bottom: 24, left: 12 }}
                >
                  <defs>
                    {renderColorGradients(['#00a8ff', '#ff9f43', '#10ac84'], 'stack')}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip
                    formatter={(value, name) => [`$ ${formatNumber(value)}`, name]}
                    labelFormatter={(label) => `Fecha: ${label}`}
                    {...wallStreetTooltipProps}
                  />
                  <Bar dataKey="mercadoPago" stackId="invoiced" fill={gradientFill('#00a8ff', 'stack')} name="MercadoPago" />
                  <Bar dataKey="pedidos" stackId="invoiced" fill={gradientFill('#ff9f43', 'stack')} name="Pedidos" />
                  <Bar dataKey="fisicas" stackId="invoiced" fill={gradientFill('#10ac84', 'stack')} name="Físicas" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            </>
            )}
          </div>
        )}

        <div className="analysis-section">
          <h4>📈 Comparación por Canal</h4>
          <div className="channel-comparison-grid">
            {channelComparison.map((channel, index) => (
              <div 
                key={index} 
                className="channel-card channel-card--accented"
                style={{ '--wsc-channel-accent': channel.color }}
              >
                <h5>
                  {channel.channel}
                </h5>
                <div className="channel-stat">
                  <span className="channel-stat-label">Ventas:</span>
                  <span className="channel-stat-value">$ {formatNumber(channel.salesAmount)}</span>
                </div>
                <div className="channel-stat">
                  <span className="channel-stat-label">Ganancia:</span>
                  <span className="channel-stat-value">$ {formatNumber(channel.profit)}</span>
                </div>
                <div className="channel-stat">
                  <span className="channel-stat-label">Margen:</span>
                  <span className={`channel-stat-value ${channel.marginPercentage > 20 ? 'margin-good' : channel.marginPercentage > 10 ? 'margin-warning' : 'margin-bad'}`}>
                    {channel.marginPercentage.toFixed(1)}%
                  </span>
                </div>
                <div className="channel-stat">
                  <span className="channel-stat-label">% del Total:</span>
                  <span className="channel-stat-value">
                    {((channel.salesAmount / calculateGeneralTotals.salesAmount) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="comparison-charts">
            <div className="comparison-chart-item">
              <h4>Distribución por Canal</h4>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <defs>
                    {renderColorGradients(channelComparison.map((e) => e.color), 'pie')}
                  </defs>
                  <Pie
                    data={channelComparison}
                    cx="50%"
                    cy="45%"
                    innerRadius={50}
                    outerRadius={70}
                    fill="#8884d8"
                    stroke="none"
                    dataKey="salesAmount"
                    label={({ channel, percent }) => `${channel}: ${(percent * 100).toFixed(1)}%`}
                    labelLine={false}
                  >
                    {channelComparison.map((entry, index) => (
                      <Cell key={`cell-${index}`} stroke="none" fill={gradientFill(entry.color, 'pie')} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChannelDistributionTooltip />} {...wallStreetTooltipProps} />
                  <Legend
                    verticalAlign="bottom"
                    height={50}
                    wrapperClassName="wallStreetChart-rechartsLegend"
                    payload={channelComparison.map((entry) => ({
                      value: entry.channel,
                      type: 'square',
                      id: entry.channel,
                      color: entry.color,
                    }))}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="comparison-chart-item">
              <h4>Evolución Diaria por Canal</h4>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={combineAllSalesData}>
                  <defs>
                    {renderColorGradients(['#00a8ff', '#ff9f43', '#10ac84'], 'stack')}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    interval="preserveStartEnd"
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={{ fontSize: 10 }} />
              <Tooltip content={<CustomCombinedTooltip />} {...wallStreetTooltipProps} />
              <Legend
                wrapperClassName="wallStreetChart-rechartsLegend"
                onClick={(e) => handleLegendClick(e.dataKey)}
                payload={[
                  { value: 'MercadoPago', type: 'square', id: 'mercadoPago', color: '#00a8ff', dataKey: 'mercadoPago' },
                  { value: 'Pedidos', type: 'square', id: 'pedidos', color: '#ff9f43', dataKey: 'pedidos' },
                  { value: 'Físicas', type: 'square', id: 'fisicas', color: '#10ac84', dataKey: 'fisicas' },
                  ...(activeLines.total
                    ? [{ value: 'Total Diario', type: 'line', id: 'total', color: 'var(--wsc-total-line)', dataKey: 'total' }]
                    : []),
                ]}
              />
                  <Bar dataKey="mercadoPago" stackId="a" fill={gradientFill('#00a8ff', 'stack')} name="MercadoPago" />
                  <Bar dataKey="pedidos" stackId="a" fill={gradientFill('#ff9f43', 'stack')} name="Pedidos" />
                  <Bar dataKey="fisicas" stackId="a" fill={gradientFill('#10ac84', 'stack')} name="Físicas" />
              {activeLines.total && (
                <Line type="monotone" dataKey="total" stroke="var(--wsc-total-line)" strokeWidth={3} name="Total Diario" />
              )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="analysis-section">
          <h4>🎯 Insights y Recomendaciones</h4>
          <div className="insights-container">
            <div className="insights-grid">
              <InsightCard icon="📊" title="Rendimiento General" status={marginStatus}>
                <p>
                  Margen de ganancia promedio:{' '}
                  <strong>{generalMarginPercentage.toFixed(1)}%</strong>
                  <span className={`insight-badge insight-badge--${marginStatus}`}>{marginLabel}</span>
                </p>
              </InsightCard>

              <InsightCard icon="📅" title="Actividad del Período">
                <p>
                  <strong>{daysWithSales}</strong> de <strong>{totalDays}</strong> días con ventas
                  {' · '}
                  <strong>{totalOperations}</strong> operaciones
                  {' · '}
                  <strong>{totalProducts}</strong> productos
                  <br />
                  Promedio diario: <strong>$ {formatNumber(dailyAverageGeneral)}</strong>
                </p>
              </InsightCard>

              {calculateGeneralTotals.discountAmount > 0 && (
                <InsightCard icon="🎫" title="Política de Descuentos">
                  <p>
                    Se aplicaron <strong>$ {formatNumber(calculateGeneralTotals.discountAmount)}</strong> en descuentos,
                    representando el <strong>
                      {((calculateGeneralTotals.discountAmount / (calculateGeneralTotals.salesAmount + calculateGeneralTotals.discountAmount)) * 100).toFixed(1)}%
                    </strong> de las ventas brutas.
                  </p>
                </InsightCard>
              )}

              <InsightCard icon="💡" title="Recomendaciones">
                {dynamicRecommendations.length > 0 ? (
                  <ul className="insight-recommendations">
                    {dynamicRecommendations.map((rec, idx) => (
                      <li key={idx}>
                        <span className="insight-recommendation-icon" aria-hidden="true">{rec.icon}</span>
                        <span>{rec.text}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>✅ Todos los indicadores del período están dentro de los parámetros saludables.</p>
                )}
              </InsightCard>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const hasNoData = !loading &&
    ventasMercadoPago.length === 0 &&
    ventasPedidos.length === 0 &&
    ventasFisicas.length === 0;

  const applyPreset = (presetFn) => {
    const { start, end } = presetFn();
    setStartDateTime(start);
    setEndDateTime(end);
    fetchSalesData(start, end);
  };

  const handleRetry = () => {
    setErrorMessage(null);
    fetchSalesData();
  };

  const buildPrintHtml = () => {
    const periodLabel = `${formatDateRangeDisplay(startDateTime)} al ${formatDateRangeDisplay(endDateTime)}`;

    // Rango real con datos: sólo se muestra si difiere del período seleccionado.
    const dataRangeLabel = (() => {
      if (!actualDateRange) return null;
      const startKey = formatDate(new Date(startDateTime));
      const endKey = formatDate(new Date(endDateTime));
      const minKey = formatDate(actualDateRange.min);
      const maxKey = formatDate(actualDateRange.max);
      return (minKey !== startKey || maxKey !== endKey) ? `${minKey} a ${maxKey}` : null;
    })();

    const now = new Date();
    const printedAt = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;

    const basePrintStyles = `
      @page { size: A4; margin: 1.4cm; }
      * { box-sizing: border-box; }
      body { font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; font-size: 11px; color: #0f172a; line-height: 1.45; }
      .print-header { border-bottom: 3px solid #2563eb; padding-bottom: 10px; margin-bottom: 14px; }
      .print-header h1 { font-size: 20px; margin: 0 0 2px 0; letter-spacing: -0.01em; color: #0f172a; }
      .print-header-meta { display: flex; flex-wrap: wrap; gap: 12px 20px; margin-top: 6px; font-size: 10.5px; color: #334155; }
      .print-header-meta span strong { color: #0f172a; font-weight: 600; }
      h2 { font-size: 13px; margin: 16px 0 6px 0; color: #0f172a; text-transform: uppercase; letter-spacing: 0.04em; }
      p { margin: 3px 0; }
      .small { font-size: 9px; color: #64748b; }
      .muted { color: #64748b; }
      .section { margin-bottom: 14px; break-inside: avoid; }
      table { width: 100%; border-collapse: collapse; margin-top: 4px; }
      th, td { border: 1px solid #e2e8f0; padding: 5px 7px; font-size: 10px; }
      th { background-color: #f1f5f9; text-align: left; font-weight: 600; color: #0f172a; }
      tbody tr:nth-child(even) { background: #f8fafc; }
      .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin: 4px 0 8px; }
      .kpi-card { border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 10px; background: #ffffff; }
      .kpi-card-label { font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; margin: 0; }
      .kpi-card-value { font-size: 14px; font-weight: 700; color: #0f172a; margin: 2px 0 0 0; font-variant-numeric: tabular-nums; }
      .kpi-card-sub { font-size: 9px; color: #64748b; margin: 2px 0 0 0; }
      .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 9px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; border: 1px solid; margin-left: 8px; vertical-align: middle; }
      .badge--good { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
      .badge--warn { background: #fffbeb; color: #b45309; border-color: #fde68a; }
      .badge--bad { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
      .recommendations { list-style: none; margin: 4px 0 0 0; padding: 0; }
      .recommendations li { padding: 6px 10px; margin: 0 0 4px 0; border-radius: 6px; background: #f8fafc; border-left: 3px solid #2563eb; font-size: 10.5px; color: #1e293b; }
      .recommendations li.rec--warn { border-left-color: #d97706; }
      .recommendations li.rec--bad { border-left-color: #dc2626; }
      .recommendations-empty { padding: 6px 10px; border-radius: 6px; background: #ecfdf5; border-left: 3px solid #10b981; color: #065f46; font-size: 10.5px; }
      .print-charts-row { display: flex; align-items: flex-end; gap: 16px; margin-top: 8px; flex-wrap: wrap; }
      .print-chart-wrap { flex-shrink: 0; }
      .print-chart-svg { display: block; height: auto; }
      .print-chart-svg--summary { max-width: 180px; }
      .print-chart-svg--donut-lg { max-width: 140px; }
      .print-chart-svg--donut-sm { max-width: 110px; }
      .print-chart-svg--full { max-width: 100%; }
      .print-chart-caption { margin: 0 0 2px 0; font-size: 10px; font-weight: 600; color: #334155; }
      .print-chart-legend { margin: 6px 0 0 0; font-size: 9px; color: #64748b; display: flex; flex-wrap: wrap; gap: 10px; }
      .print-chart-legend span { display: inline-flex; align-items: center; gap: 4px; }
      .print-chart-legend i { display: inline-block; width: 9px; height: 9px; border-radius: 2px; }
      .print-footer { margin-top: 20px; padding-top: 8px; border-top: 1px solid #e2e8f0; font-size: 9px; color: #64748b; display: flex; justify-content: space-between; }
    `;

    if (calculateGeneralTotals.salesAmount === 0) {
      return `
<!DOCTYPE html>
<html lang="es">
<head>
  <meta charSet="utf-8" />
  <title>Informe de Balance</title>
  <style>${basePrintStyles}</style>
</head>
<body>
  <div class="print-header">
    <h1>Informe de Balance</h1>
    <div class="print-header-meta">
      <span><strong>Período:</strong> ${periodLabel}</span>
      <span class="small">Impreso el ${printedAt}</span>
    </div>
  </div>
  <p>No hay ventas registradas en el período seleccionado.</p>
</body>
</html>
`;
    }

    // ===== MÉTRICAS CENTRALES =====
    const generalMarginPercentage = calculateGeneralTotals.salesAmount > 0
      ? (calculateGeneralTotals.profit / calculateGeneralTotals.salesAmount * 100)
      : 0;

    const totalDaysCalendar = getTotalDaysInRange();
    const daysWithSales = new Set(
      [...ventasMercadoPago, ...ventasPedidos, ...ventasFisicas]
        .filter((d) => (d.salesAmount || 0) > 0)
        .map((d) => d.date)
    ).size;

    const totalOperations =
      (totalsMercadoPago.salesCount || 0) +
      (totalsPedidos.salesCount || 0) +
      (totalsFisicas.salesCount || 0);
    const totalProducts =
      (totalsMercadoPago.productsCount || 0) +
      (totalsPedidos.productsCount || 0) +
      (totalsFisicas.productsCount || 0);

    const dailyAverageGeneral = daysWithSales > 0 ? calculateGeneralTotals.salesAmount / daysWithSales : 0;
    const averageTicket = totalOperations > 0 ? calculateGeneralTotals.salesAmount / totalOperations : 0;
    const productsPerOperation = totalOperations > 0 ? totalProducts / totalOperations : 0;
    const monthlyAverageSales = totalMonthsInRange > 0 ? calculateGeneralTotals.salesAmount / totalMonthsInRange : 0;

    // Estado del margen (reutiliza los umbrales centrales)
    let marginStatus = 'bad';
    let marginLabel = 'Mejorable';
    if (generalMarginPercentage > INSIGHT_THRESHOLDS.MARGIN_EXCELLENT) {
      marginStatus = 'good';
      marginLabel = 'Excelente';
    } else if (generalMarginPercentage > INSIGHT_THRESHOLDS.MARGIN_GOOD) {
      marginStatus = 'warn';
      marginLabel = 'Bueno';
    }

    // Canales
    const bestChannel = channelComparison.reduce((prev, cur) => (prev.salesAmount > cur.salesAmount) ? prev : cur);
    const mostProfitableChannel = channelComparison.reduce((prev, cur) => (prev.marginPercentage > cur.marginPercentage) ? prev : cur);

    // Métodos de pago (detalle completo)
    const paymentMethodMeta = {
      efectivo: { label: 'Efectivo', color: '#2ecc71' },
      transferencia: { label: 'Transferencia', color: '#3498db' },
      debito: { label: 'Débito', color: '#e74c3c' },
      credito: { label: 'Crédito', color: '#f39c12' },
      consignacion: { label: 'Consignación (no cobrada)', color: '#8b5cf6' },
    };
    const paymentTotal = Object.values(paymentMethodsAnalysis || {}).reduce((s, m) => s + (m.totalAmount || 0), 0);
    const paymentBreakdown = Object.entries(paymentMethodMeta)
      .map(([key, meta]) => {
        const data = paymentMethodsAnalysis?.[key] || { totalAmount: 0, totalCount: 0 };
        return {
          key,
          label: meta.label,
          color: meta.color,
          amount: data.totalAmount || 0,
          count: data.totalCount || 0,
          pct: paymentTotal > 0 ? (data.totalAmount / paymentTotal * 100) : 0,
        };
      })
      .filter((m) => m.amount > 0);
    const hasPaymentData = paymentBreakdown.length > 0;
    const cashTotal = paymentMethodsAnalysis?.efectivo?.totalAmount || 0;
    const digitalTotal =
      (paymentMethodsAnalysis?.transferencia?.totalAmount || 0) +
      (paymentMethodsAnalysis?.debito?.totalAmount || 0) +
      (paymentMethodsAnalysis?.credito?.totalAmount || 0);
    const cashPct = paymentTotal > 0 ? (cashTotal / paymentTotal * 100) : 0;
    const digitalPct = paymentTotal > 0 ? (digitalTotal / paymentTotal * 100) : 0;

    // AFIP
    const totalInvoicedPct = calculateGeneralTotals.salesAmount > 0
      ? (totalInvoicedAll / calculateGeneralTotals.salesAmount * 100)
      : 0;
    const invoicedChannels = [
      { name: 'MercadoPago', amount: totalsMercadoPago.invoicedAmount || 0, count: totalsMercadoPago.invoicedCount || 0 },
      { name: 'Pedidos', amount: totalsPedidos.invoicedAmount || 0, count: totalsPedidos.invoicedCount || 0 },
      { name: 'Físicas', amount: totalsFisicas.invoicedAmount || 0, count: totalsFisicas.invoicedCount || 0 },
    ].filter((c) => c.amount > 0 || c.count > 0);
    const topInvoicedChannel = invoicedChannels.length > 0
      ? invoicedChannels.reduce((prev, cur) => (cur.amount > prev.amount) ? cur : prev)
      : null;

    // Mensuales
    const hasMonthlyData = monthlyData.length > 0;
    const bestMonth = hasMonthlyData
      ? monthlyData.reduce((prev, cur) => (cur.totalSales > prev.totalSales) ? cur : prev)
      : null;
    const mostProfitableMonth = hasMonthlyData
      ? monthlyData.reduce((prev, cur) => (cur.totalProfit > prev.totalProfit) ? cur : prev)
      : null;

    // Tendencia: compara la primera mitad vs la segunda mitad (requiere al menos 2 meses)
    let trendLabel = null;
    let trendDelta = 0;
    if (hasMonthlyData && monthlyData.length >= 2) {
      const mid = Math.floor(monthlyData.length / 2);
      const firstHalf = monthlyData.slice(0, mid);
      const secondHalf = monthlyData.slice(mid);
      const avgFirst = firstHalf.reduce((s, m) => s + m.totalSales, 0) / firstHalf.length;
      const avgSecond = secondHalf.reduce((s, m) => s + m.totalSales, 0) / secondHalf.length;
      if (avgFirst > 0) {
        trendDelta = ((avgSecond - avgFirst) / avgFirst) * 100;
        if (Math.abs(trendDelta) < 3) trendLabel = 'Estable';
        else if (trendDelta > 0) trendLabel = 'Creciente';
        else trendLabel = 'Decreciente';
      }
    }

    // Descuentos
    const discountAmount = calculateGeneralTotals.discountAmount || 0;
    const grossSales = calculateGeneralTotals.salesAmount + discountAmount;
    const discountPct = grossSales > 0 ? (discountAmount / grossSales * 100) : 0;

    // ===== RECOMENDACIONES DINÁMICAS (misma lógica que la UI) =====
    const recommendations = [];
    if (generalMarginPercentage < INSIGHT_THRESHOLDS.MARGIN_GOOD) {
      recommendations.push({
        level: 'bad',
        text: `Revisar estructura de costos: el margen actual (${generalMarginPercentage.toFixed(1)}%) está por debajo del umbral objetivo.`,
      });
    }
    if (bestChannel.channel !== mostProfitableChannel.channel) {
      recommendations.push({
        level: 'warn',
        text: `Potenciar ${mostProfitableChannel.channel}: concentra el mejor margen (${mostProfitableChannel.marginPercentage.toFixed(1)}%) aunque no lidera en volumen.`,
      });
    }
    const daysWithoutSales = Math.max(0, totalDaysCalendar - daysWithSales);
    const daysWithoutSalesRatio = totalDaysCalendar > 0 ? daysWithoutSales / totalDaysCalendar : 0;
    if (daysWithoutSalesRatio > INSIGHT_THRESHOLDS.DAYS_WITHOUT_SALES_ALERT) {
      recommendations.push({
        level: 'warn',
        text: `Analizar los ${daysWithoutSales} días sin ventas (${(daysWithoutSalesRatio * 100).toFixed(0)}% del período) para detectar patrones.`,
      });
    }
    if (discountAmount > calculateGeneralTotals.profit * INSIGHT_THRESHOLDS.DISCOUNT_VS_PROFIT_HEAVY) {
      recommendations.push({
        level: 'warn',
        text: 'Evaluar política de descuentos: su impacto sobre la rentabilidad es significativo.',
      });
    }
    if (trendLabel === 'Decreciente' && Math.abs(trendDelta) > 10) {
      recommendations.push({
        level: 'bad',
        text: `Tendencia decreciente: las ventas cayeron ${Math.abs(trendDelta).toFixed(1)}% entre la primera y segunda mitad del período.`,
      });
    }
    if (trendLabel === 'Creciente' && trendDelta > 10) {
      recommendations.push({
        level: 'good',
        text: `Buen momentum: las ventas crecieron ${trendDelta.toFixed(1)}% en la segunda mitad del período.`,
      });
    }

    // ===== GRÁFICOS SVG =====
    const channelRows = channelComparison.map((ch) => `
      <tr>
        <td>${ch.channel}</td>
        <td>$ ${formatNumber(ch.salesAmount)}</td>
        <td>$ ${formatNumber(ch.profit)}</td>
        <td>${ch.marginPercentage.toFixed(1)}%</td>
        <td>${ch.sharePercentage.toFixed(1)}%</td>
      </tr>
    `).join('');

    // Barras verticales Ventas / Costo / Ganancia (resumen)
    const summaryMax = Math.max(
      calculateGeneralTotals.salesAmount,
      calculateGeneralTotals.costPrice,
      calculateGeneralTotals.profit,
      1
    );
    const svgBarSummary = (() => {
      const h = 64, w = 32, gap = 14, labels = ['Ventas', 'Costo', 'Ganancia'];
      const vals = [calculateGeneralTotals.salesAmount, calculateGeneralTotals.costPrice, calculateGeneralTotals.profit];
      const cols = ['#3b82f6', '#ef4444', '#10b981'];
      return `<svg class="print-chart-svg print-chart-svg--summary" viewBox="0 0 140 ${h + 28}" width="100%">
        ${vals.map((v, i) => {
          const bh = summaryMax > 0 ? (v / summaryMax) * h : 0;
          const x = 14 + i * (w + gap);
          const y = h + 16 - bh;
          return `<g>
            <rect x="${x}" y="${y}" width="${w}" height="${Math.max(bh, 2)}" rx="3" fill="${cols[i]}"/>
            <text x="${x + w / 2}" y="${h + 24}" text-anchor="middle" font-size="7" fill="#475569">${labels[i]}</text>
          </g>`;
        }).join('')}
      </svg>`;
    })();

    // Donut canales
    const donutR = 32;
    const donutStroke = 14;
    const donutC = 2 * Math.PI * donutR;
    const svgDonutChannels = (() => {
      let offset = 0;
      const segs = channelComparison.map((ch) => {
        const len = (ch.sharePercentage / 100) * donutC;
        const seg = `<circle cx="50" cy="50" r="${donutR}" fill="none" stroke="${ch.color}" stroke-width="${donutStroke}" stroke-dasharray="${len} ${donutC}" stroke-dashoffset="${-offset}"/>`;
        offset += len;
        return seg;
      }).join('');
      return `<svg class="print-chart-svg print-chart-svg--donut-lg" viewBox="0 0 100 100" width="100%">
        <circle cx="50" cy="50" r="${donutR}" fill="none" stroke="#e5e7eb" stroke-width="${donutStroke}"/>
        ${segs}
        <text x="50" y="52" text-anchor="middle" font-size="10" font-weight="bold" fill="#0f172a">Canales</text>
      </svg>`;
    })();

    // Donut métodos de pago (usa breakdown completo, no sólo cash/digital)
    const svgDonutPayment = !hasPaymentData ? '' : (() => {
      let offset = 0;
      const segs = paymentBreakdown.map((m) => {
        const len = (m.pct / 100) * donutC;
        const seg = `<circle cx="50" cy="50" r="${donutR}" fill="none" stroke="${m.color}" stroke-width="${donutStroke}" stroke-dasharray="${len} ${donutC}" stroke-dashoffset="${-offset}"/>`;
        offset += len;
        return seg;
      }).join('');
      return `<svg class="print-chart-svg print-chart-svg--donut-sm" viewBox="0 0 100 100" width="100%">
        <circle cx="50" cy="50" r="${donutR}" fill="none" stroke="#e5e7eb" stroke-width="${donutStroke}"/>
        ${segs}
        <text x="50" y="52" text-anchor="middle" font-size="9" font-weight="bold" fill="#0f172a">Pagos</text>
      </svg>`;
    })();

    // Chart mensual (dos paneles apilados: stack por canal + V/C/G)
    const monthlyChartW = 520;
    const monthlyChartH = 150;
    const monthlyMargin = { left: 48, right: 14, top: 6, bottom: 24 };
    const monthlyPlotH = monthlyChartH - monthlyMargin.top - monthlyMargin.bottom;
    const monthlyPlotW = monthlyChartW - monthlyMargin.left - monthlyMargin.right;
    const nMonths = monthlyData.length;
    const monthlyBarSlot = nMonths > 0 ? monthlyPlotW / nMonths : 0;
    const baseY = monthlyChartH - monthlyMargin.bottom;
    const formatAxis = (v) => v >= 1e6 ? (v / 1e6).toFixed(1) + 'M' : v >= 1e3 ? (v / 1e3).toFixed(0) + 'k' : String(v);

    const buildGrid = (maxVal) => {
      const ticks = 4;
      let out = '';
      for (let t = 0; t <= ticks; t++) {
        const y = monthlyMargin.top + (1 - t / ticks) * monthlyPlotH;
        const val = (t / ticks) * maxVal;
        out += `<line x1="${monthlyMargin.left}" y1="${y}" x2="${monthlyMargin.left + monthlyPlotW}" y2="${y}" stroke="#e2e8f0" stroke-dasharray="2 2"/>`;
        out += `<text x="${monthlyMargin.left - 4}" y="${y + 3}" text-anchor="end" font-size="8" fill="#64748b">${formatAxis(val)}</text>`;
      }
      return out;
    };

    const buildLabelsX = (slotCalc) => {
      const step = Math.max(1, Math.floor(nMonths / 10));
      let out = '';
      monthlyData.forEach((m, i) => {
        if (i % step !== 0 && i !== nMonths - 1) return;
        out += `<text x="${slotCalc(i)}" y="${baseY + 14}" text-anchor="middle" font-size="7" fill="#64748b">${m.month}</text>`;
      });
      return out;
    };

    let svgMonthlyStacked = '';
    let svgMonthlyTriple = '';
    if (hasMonthlyData) {
      const maxStack = Math.max(...monthlyData.map((m) => (m.mercadoPago || 0) + (m.pedidos || 0) + (m.fisicas || 0)), 1);
      const maxTriple = Math.max(...monthlyData.flatMap((m) => [m.totalSales || 0, m.totalCost || 0, m.totalProfit || 0]), 1);
      const barW1 = Math.min(16, Math.max(4, monthlyBarSlot * 0.55));

      let barsStacked = '';
      monthlyData.forEach((m, i) => {
        const x = monthlyMargin.left + (i + 0.5) * monthlyBarSlot - barW1 / 2;
        const layers = [
          { val: m.fisicas || 0, color: '#10ac84' },
          { val: m.pedidos || 0, color: '#ff9f43' },
          { val: m.mercadoPago || 0, color: '#00a8ff' },
        ];
        let y = baseY;
        layers.forEach(({ val, color }) => {
          if (val <= 0) return;
          const h = (val / maxStack) * monthlyPlotH;
          barsStacked += `<rect x="${x}" y="${y - h}" width="${barW1}" height="${Math.max(h, 1)}" fill="${color}"/>`;
          y -= h;
        });
      });
      svgMonthlyStacked = `<svg class="print-chart-svg print-chart-svg--full" viewBox="0 0 ${monthlyChartW} ${monthlyChartH}" width="100%">
        ${buildGrid(maxStack)}
        ${barsStacked}
        <line x1="${monthlyMargin.left}" y1="${baseY}" x2="${monthlyMargin.left + monthlyPlotW}" y2="${baseY}" stroke="#94a3b8"/>
        ${buildLabelsX((i) => monthlyMargin.left + (i + 0.5) * monthlyBarSlot)}
      </svg>`;

      const barW3 = Math.max(2, (monthlyBarSlot - 8) / 3);
      const gap3 = 2;
      let barsTriple = '';
      monthlyData.forEach((m, i) => {
        const slotCenter = monthlyMargin.left + (i + 0.5) * monthlyBarSlot;
        const x0 = slotCenter - (barW3 * 3 + gap3 * 2) / 2;
        const metrics = [
          { val: m.totalSales || 0, color: '#3b82f6' },
          { val: m.totalCost || 0, color: '#ef4444' },
          { val: m.totalProfit || 0, color: '#10b981' },
        ];
        metrics.forEach((mt, j) => {
          const h = (mt.val / maxTriple) * monthlyPlotH;
          barsTriple += `<rect x="${x0 + j * (barW3 + gap3)}" y="${baseY - Math.max(h, 1)}" width="${barW3}" height="${Math.max(h, 1)}" fill="${mt.color}" rx="1"/>`;
        });
      });
      svgMonthlyTriple = `<svg class="print-chart-svg print-chart-svg--full" viewBox="0 0 ${monthlyChartW} ${monthlyChartH}" width="100%">
        ${buildGrid(maxTriple)}
        ${barsTriple}
        <line x1="${monthlyMargin.left}" y1="${baseY}" x2="${monthlyMargin.left + monthlyPlotW}" y2="${baseY}" stroke="#94a3b8"/>
        ${buildLabelsX((i) => monthlyMargin.left + (i + 0.5) * monthlyBarSlot)}
      </svg>`;
    }

    // Leyendas
    const channelLegend = channelComparison
      .map((ch) => `<span><i style="background:${ch.color}"></i>${ch.channel}</span>`).join('');
    const paymentLegend = paymentBreakdown
      .map((m) => `<span><i style="background:${m.color}"></i>${m.label}</span>`).join('');
    const monthlyStackedLegend = `
      <span><i style="background:#00a8ff"></i>MercadoPago</span>
      <span><i style="background:#ff9f43"></i>Pedidos</span>
      <span><i style="background:#10ac84"></i>Físicas</span>
    `;
    const monthlyTripleLegend = `
      <span><i style="background:#3b82f6"></i>Ventas</span>
      <span><i style="background:#ef4444"></i>Costo</span>
      <span><i style="background:#10b981"></i>Ganancia</span>
    `;

    return `
<!DOCTYPE html>
<html lang="es">
<head>
  <meta charSet="utf-8" />
  <title>Informe de Balance</title>
  <style>${basePrintStyles}</style>
</head>
<body>
  <div class="print-header">
    <h1>Informe de Balance</h1>
    <div class="print-header-meta">
      <span><strong>Período:</strong> ${periodLabel}</span>
      ${dataRangeLabel ? `<span><strong>Datos reales:</strong> ${dataRangeLabel}</span>` : ''}
      <span class="small">Impreso el ${printedAt}</span>
    </div>
  </div>

  <div class="section">
    <h2>Resumen ejecutivo <span class="badge badge--${marginStatus}">${marginLabel}</span></h2>
    <div class="kpi-grid">
      <div class="kpi-card">
        <p class="kpi-card-label">Ventas totales</p>
        <p class="kpi-card-value">$ ${formatNumber(calculateGeneralTotals.salesAmount)}</p>
      </div>
      <div class="kpi-card">
        <p class="kpi-card-label">Costo total</p>
        <p class="kpi-card-value">$ ${formatNumber(calculateGeneralTotals.costPrice)}</p>
      </div>
      <div class="kpi-card">
        <p class="kpi-card-label">Ganancia</p>
        <p class="kpi-card-value">$ ${formatNumber(calculateGeneralTotals.profit)}</p>
        <p class="kpi-card-sub">Margen ${generalMarginPercentage.toFixed(1)}%</p>
      </div>
      <div class="kpi-card">
        <p class="kpi-card-label">Ticket promedio</p>
        <p class="kpi-card-value">$ ${formatNumber(averageTicket)}</p>
        <p class="kpi-card-sub">${productsPerOperation.toFixed(1)} productos / operación</p>
      </div>
      <div class="kpi-card">
        <p class="kpi-card-label">Días con ventas</p>
        <p class="kpi-card-value">${daysWithSales} <span class="muted" style="font-size:11px;font-weight:500;">/ ${totalDaysCalendar}</span></p>
      </div>
      <div class="kpi-card">
        <p class="kpi-card-label">Operaciones</p>
        <p class="kpi-card-value">${totalOperations}</p>
        <p class="kpi-card-sub">${totalProducts} productos</p>
      </div>
      <div class="kpi-card">
        <p class="kpi-card-label">Promedio diario</p>
        <p class="kpi-card-value">$ ${formatNumber(dailyAverageGeneral)}</p>
      </div>
      ${totalMonthsInRange > 1 ? `
      <div class="kpi-card">
        <p class="kpi-card-label">Promedio mensual</p>
        <p class="kpi-card-value">$ ${formatNumber(monthlyAverageSales)}</p>
        ${trendLabel ? `<p class="kpi-card-sub">Tendencia: ${trendLabel}${trendDelta !== 0 ? ` (${trendDelta > 0 ? '+' : ''}${trendDelta.toFixed(1)}%)` : ''}</p>` : ''}
      </div>
      ` : `
      <div class="kpi-card">
        <p class="kpi-card-label">% Facturado</p>
        <p class="kpi-card-value">${totalInvoicedPct.toFixed(1)}%</p>
        <p class="kpi-card-sub">$ ${formatNumber(totalInvoicedAll)}</p>
      </div>
      `}
    </div>
    <div class="print-charts-row">
      <div class="print-chart-wrap">
        <p class="print-chart-caption">Distribución Ventas · Costo · Ganancia</p>
        ${svgBarSummary}
      </div>
    </div>
    ${discountAmount > 0 ? `
    <p class="small" style="margin-top:6px;"><strong>Descuentos aplicados:</strong> $ ${formatNumber(discountAmount)} (${discountPct.toFixed(1)}% de las ventas brutas)</p>
    ` : ''}
  </div>

  <div class="section">
    <h2>Canales de venta</h2>
    <table>
      <thead>
        <tr>
          <th>Canal</th>
          <th>Ventas</th>
          <th>Ganancia</th>
          <th>Margen</th>
          <th>% del total</th>
        </tr>
      </thead>
      <tbody>
        ${channelRows}
      </tbody>
    </table>
    <div class="print-charts-row">
      <div class="print-chart-wrap">
        <p class="print-chart-caption">Distribución por canal</p>
        ${svgDonutChannels}
        <div class="print-chart-legend">${channelLegend}</div>
      </div>
    </div>
  </div>

  ${(totalInvoicedAll > 0 || totalInvoicedCountAll > 0) ? `
  <div class="section">
    <h2>Facturación AFIP</h2>
    <table>
      <tbody>
        <tr><th>Monto facturado</th><td>$ ${formatNumber(totalInvoicedAll)}</td></tr>
        <tr><th>Comprobantes emitidos</th><td>${totalInvoicedCountAll}</td></tr>
        <tr><th>% de ventas facturadas</th><td>${totalInvoicedPct.toFixed(1)}%</td></tr>
        ${topInvoicedChannel ? `<tr><th>Canal con mayor facturación</th><td>${topInvoicedChannel.name} · $ ${formatNumber(topInvoicedChannel.amount)} (${topInvoicedChannel.count} comprobantes)</td></tr>` : ''}
      </tbody>
    </table>
  </div>
  ` : ''}

  ${hasPaymentData ? `
  <div class="section">
    <h2>Métodos de pago · Ventas físicas</h2>
    <table>
      <thead>
        <tr>
          <th>Método</th>
          <th>Monto</th>
          <th>Operaciones</th>
          <th>Ticket promedio</th>
          <th>% del total</th>
        </tr>
      </thead>
      <tbody>
        ${paymentBreakdown.map((m) => `
          <tr>
            <td>${m.label}</td>
            <td>$ ${formatNumber(m.amount)}</td>
            <td>${m.count}</td>
            <td>$ ${formatNumber(m.count > 0 ? m.amount / m.count : 0)}</td>
            <td>${m.pct.toFixed(1)}%</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
    <p class="small" style="margin-top:4px;">Efectivo <strong>${cashPct.toFixed(1)}%</strong> · Canales digitales <strong>${digitalPct.toFixed(1)}%</strong></p>
    <div class="print-charts-row">
      <div class="print-chart-wrap">
        <p class="print-chart-caption">Distribución por método</p>
        ${svgDonutPayment}
        <div class="print-chart-legend">${paymentLegend}</div>
      </div>
    </div>
  </div>
  ` : ''}

  ${hasMonthlyData ? `
  <div class="section">
    <h2>Evolución mensual</h2>
    <p class="print-chart-caption">Ventas por canal</p>
    ${svgMonthlyStacked}
    <div class="print-chart-legend">${monthlyStackedLegend}</div>

    <p class="print-chart-caption" style="margin-top:10px;">Ventas · Costo · Ganancia por mes</p>
    ${svgMonthlyTriple}
    <div class="print-chart-legend">${monthlyTripleLegend}</div>

    <div class="kpi-grid" style="margin-top:10px;">
      ${bestMonth ? `
      <div class="kpi-card">
        <p class="kpi-card-label">Mejor mes (ventas)</p>
        <p class="kpi-card-value">${bestMonth.month}</p>
        <p class="kpi-card-sub">$ ${formatNumber(bestMonth.totalSales)}</p>
      </div>
      ` : ''}
      ${mostProfitableMonth ? `
      <div class="kpi-card">
        <p class="kpi-card-label">Mes más rentable</p>
        <p class="kpi-card-value">${mostProfitableMonth.month}</p>
        <p class="kpi-card-sub">$ ${formatNumber(mostProfitableMonth.totalProfit)} de ganancia</p>
      </div>
      ` : ''}
      ${trendLabel ? `
      <div class="kpi-card">
        <p class="kpi-card-label">Tendencia</p>
        <p class="kpi-card-value">${trendLabel}</p>
        <p class="kpi-card-sub">${trendDelta > 0 ? '+' : ''}${trendDelta.toFixed(1)}% entre mitades</p>
      </div>
      ` : ''}
    </div>
  </div>
  ` : ''}

  <div class="section">
    <h2>Insights y recomendaciones</h2>
    ${recommendations.length > 0 ? `
    <ul class="recommendations">
      ${recommendations.map((r) => `<li class="rec--${r.level}">${r.text}</li>`).join('')}
    </ul>
    ` : `
    <div class="recommendations-empty">Todos los indicadores del período están dentro de los parámetros saludables.</div>
    `}
  </div>

  <div class="print-footer">
    <span>Informe generado por Privistore</span>
    <span>${printedAt}</span>
  </div>
</body>
</html>
`;
  };

  const handlePrintAnalysis = () => {
    try {
      const html = buildPrintHtml();
      const printWindow = window.open('', '_blank', 'width=900,height=700');
      if (!printWindow) {
        // Popup bloqueado
        return;
      }
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    } catch (e) {
      console.error('Error al generar el informe para imprimir:', e);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="wallStreetChart-container">
      <h3 className="wallStreetChart-title">Balance</h3>

      {errorMessage && (
        <div className="wallStreetChart-error" role="alert">
          <p className="wallStreetChart-errorMessage">{errorMessage}</p>
          <button type="button" className="wallStreetChart-retryButton" onClick={handleRetry}>
            Reintentar
          </button>
        </div>
      )}

      <div className="wallStreetChart-layout">
        <aside className="wallStreetChart-sidebar" aria-label="Filtros y vistas">
          <div className="wallStreetChart-sidebarInner">
            <div className="wallStreetChart-sidebarSection">
              <span className="wallStreetChart-sidebarHeading">Período</span>
              <label className="wallStreetChart-label">
                <span className="wallStreetChart-labelText">Desde</span>
                <input
                  type="datetime-local"
                  value={startDateTime}
                  onChange={(e) => setStartDateTime(e.target.value)}
                  aria-label="Fecha y hora de inicio"
                  className="wallStreetChart-filterInput"
                />
              </label>
              <label className="wallStreetChart-label">
                <span className="wallStreetChart-labelText">Hasta</span>
                <input
                  type="datetime-local"
                  value={endDateTime}
                  onChange={(e) => setEndDateTime(e.target.value)}
                  aria-label="Fecha y hora de fin"
                  className="wallStreetChart-filterInput"
                />
              </label>
              <button
                type="button"
                className="wallStreetChart-updateButton"
                onClick={() => fetchSalesData()}
              >
                Actualizar datos
              </button>
            </div>

            <div className="wallStreetChart-sidebarSection">
              <span className="wallStreetChart-sidebarHeading">Rango rápido</span>
              <div className="wallStreetChart-presetBtns wallStreetChart-presetBtns--stacked">
                <button type="button" className="wallStreetChart-presetBtn" onClick={() => applyPreset(getPresetEsteMes)}>
                  Este mes
                </button>
                <button type="button" className="wallStreetChart-presetBtn" onClick={() => applyPreset(getPresetMesPasado)}>
                  Mes pasado
                </button>
                <button type="button" className="wallStreetChart-presetBtn" onClick={() => applyPreset(getPresetUltimos3Meses)}>
                  Últimos 3 meses
                </button>
                <button type="button" className="wallStreetChart-presetBtn" onClick={() => applyPreset(getPresetEsteAnio)}>
                  Este año
                </button>
              </div>
            </div>

            {!hasNoData && (
              <div className="wallStreetChart-sidebarSection">
                <span className="wallStreetChart-sidebarHeading">Temporalidad</span>
                <div className="wallStreetChart-granularityBtns wallStreetChart-granularityBtns--stacked" role="group" aria-label="Temporalidad del gráfico">
                  {GRANULARITY_OPTIONS.map(({ value, label: optionLabel }) => (
                    <button
                      key={value}
                      type="button"
                      className={`wallStreetChart-granularityBtn ${chartGranularity === value ? 'wallStreetChart-granularityBtn--active' : ''}`}
                      onClick={() => setChartGranularity(value)}
                      aria-pressed={chartGranularity === value}
                    >
                      {optionLabel}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!hasNoData && (
              <div className="wallStreetChart-sidebarSection">
                <span className="wallStreetChart-sidebarHeading">Vista</span>
                <div className="wallStreetChart-granularityBtns wallStreetChart-granularityBtns--stacked" role="group" aria-label="Tipo de gráfico">
                  {CHART_TYPE_OPTIONS.map(({ value, label: optionLabel }) => (
                    <button
                      key={value}
                      type="button"
                      className={`wallStreetChart-granularityBtn ${chartType === value ? 'wallStreetChart-granularityBtn--active' : ''}`}
                      onClick={() => setChartType(value)}
                      aria-pressed={chartType === value}
                    >
                      {optionLabel}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!hasNoData && (
              <div className="wallStreetChart-sidebarSection wallStreetChart-sidebarSection--actions">
                <button
                  type="button"
                  className="wallStreetChart-printButton"
                  onClick={handlePrintAnalysis}
                >
                  Imprimir análisis
                </button>
              </div>
            )}

            <span className="wallStreetChart-dateRangeLabel" role="status">
              {actualDateRange
                ? `Datos desde ${formatDate(actualDateRange.min)} hasta ${formatDate(actualDateRange.max)}.`
                : 'No hay datos en el período seleccionado.'}
            </span>
          </div>
        </aside>

        <div className="wallStreetChart-main">
          {hasNoData ? (
            <div className="wallStreetChart-emptyState" role="status">
              <p className="wallStreetChart-emptyStateTitle">No hay ventas en el período seleccionado</p>
              <p className="wallStreetChart-emptyStateText">
                Prueba otro rango de fechas con los botones de la barra lateral o cambia las fechas manualmente.
              </p>
            </div>
          ) : (
            <>
              {renderTotalsHeader()}

              <div className="wallStreetChart-carousel" role="region" aria-label="Gráficos por canal de ventas">
                <div className="wallStreetChart-carouselHeader">
                  <button
                    type="button"
                    className="wallStreetChart-carouselBtn wallStreetChart-carouselBtn--prev"
                    onClick={() => setChartCarouselIndex((i) => (i === 0 ? CHART_CHANNEL_LAST : i - 1))}
                    aria-label="Gráfico anterior"
                  >
                    ‹
                  </button>
                  <div className="wallStreetChart-carouselDots">
                    {CHART_CHANNEL_TABS.map(({ label, index }) => (
                      <button
                        key={index}
                        type="button"
                        className={`wallStreetChart-carouselDot ${chartCarouselIndex === index ? 'wallStreetChart-carouselDot--active' : ''}`}
                        onClick={() => setChartCarouselIndex(index)}
                        aria-label={`Ver ${label}`}
                        aria-current={chartCarouselIndex === index ? 'true' : undefined}
                      >
                        <span className="wallStreetChart-carouselDotLabel">{label}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="wallStreetChart-carouselBtn wallStreetChart-carouselBtn--next"
                    onClick={() => setChartCarouselIndex((i) => (i === CHART_CHANNEL_LAST ? 0 : i + 1))}
                    aria-label="Siguiente gráfico"
                  >
                    ›
                  </button>
                </div>
                <div className="wallStreetChart-carouselTrack">
                  {chartCarouselIndex === 0 && (
                    <div className="wallStreetChart-carouselSlide wallStreetChart-carouselSlide--muted">
                      {renderChart(ventasTodas)}
                    </div>
                  )}
                  {chartCarouselIndex === 1 && (
                    <div className="wallStreetChart-carouselSlide wallStreetChart-carouselSlide--muted">
                      {renderChart(ventasFisicas)}
                    </div>
                  )}
                  {chartCarouselIndex === 2 && (
                    <div className="wallStreetChart-carouselSlide wallStreetChart-carouselSlide--muted">
                      {renderChart(ventasMercadoPago)}
                    </div>
                  )}
                  {chartCarouselIndex === 3 && (
                    <div className="wallStreetChart-carouselSlide wallStreetChart-carouselSlide--muted">
                      {renderChart(ventasPedidos)}
                    </div>
                  )}
                  {chartCarouselIndex === 4 && (
                    <div className="wallStreetChart-carouselSlide wallStreetChart-carouselSlide--muted">
                      {ventasConsignacion.length === 0 ? (
                        <div className="wallStreetChart-emptyState" role="status">
                          <p className="wallStreetChart-emptyStateTitle">No hay consignaciones en este período</p>
                          <p className="wallStreetChart-emptyStateText">
                            Las ventas en consignación siguen contando en Todas y en Físicas. Esta pestaña muestra solo ese canal y las velas de lo no cobrado.
                          </p>
                        </div>
                      ) : (
                        renderChart(ventasConsignacion)
                      )}
                    </div>
                  )}
                </div>
              </div>

              {renderPaymentMethodsAnalysis()}
              {renderCompleteAnalysis()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const pad = (n) => String(n).padStart(2, '0');

const getDefaultStartDateTime = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01T00:00`;
};

const getDefaultEndDateTime = () => {
  const now = new Date();
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(lastDayOfMonth)}T23:59`;
};

/** Presets de rango de fechas (retornan { start, end } en formato datetime-local) */
const getPresetEsteMes = () => {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return {
    start: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01T00:00`,
    end: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(lastDay)}T23:59`,
  };
};

const getPresetMesPasado = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return {
    start: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01T00:00`,
    end: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(lastDay)}T23:59`,
  };
};

const getPresetUltimos3Meses = () => {
  const end = new Date();
  const start = new Date(end);
  start.setMonth(start.getMonth() - 2);
  start.setDate(1);
  start.setHours(0, 0, 0, 0);
  return {
    start: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}T00:00`,
    end: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}T23:59`,
  };
};

const getPresetEsteAnio = () => {
  const now = new Date();
  return {
    start: `${now.getFullYear()}-01-01T00:00`,
    end: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T23:59`,
  };
};

export default WallStreetChart;